import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { DateAdapter } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReferenceItem, OrganizationUnitItem } from '@features/user/models/filter-data.model';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { UnitSelectorComponent, UnitSelectorSelection } from '@shared/components/unit-selector/unit-selector.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { AppointmentAreaDialogComponent, AppointmentAreaDialogData } from './appointment-area-dialog.component';
import { MembershipCommunityDialogComponent, MembershipCommunityDialogData } from './membership-community-dialog.component';
import { NotificationService } from '@shared/services/notification.service';
import { OrganizationUnitService } from '@shared/services/organization-unit.service';
import { getPersonEventEditorErrorMessage } from '../../pages/person-event-editor-page/person-event-editor.utils';
import {
  MinisterialActivityArea,
  MinisterialActivityActionDefinition,
  MinisterialActivityEffectiveState,
  MinisterialActivityEffectiveDesignation,
  MinisterialActivityKind,
  MinisterialActivityTransitionPreview,
  MinistryEventType,
  PersonEvent,
} from '../../models/person-event.model';
import { Person } from '../../models/person.model';
import { PersonService } from '../../services/person.service';
import {
  applyMinisterialActivitySummaryTemplate,
  ministerialActivitySummaryConfig,
  MinisterialActivitySummaryContext,
} from './ministerial-activity-summary.config';
import {
  actionPresentationFor,
  compareMinisterialActivityActions,
  hiddenMinisterialActivityActions,
  MinisterialActivityActionGroup,
} from './ministerial-activity-actions.config';

type MinisterialActivityRequest = Parameters<PersonService['createMinisterialActivityEvent']>[1];

type ActPlaceMode = 'none' | 'community' | 'location';
type MinisterialMobileStep = 'action' | 'form' | 'summary';

interface MinisterialActionOption {
  value: MinistryEventType;
  label: string;
  description: string;
  icon: string;
  group: MinisterialActivityActionGroup;
}

interface ActivityReference {
  kind: MinisterialActivityKind;
  id: number;
  name: string;
  isActive: boolean;
  allowedOrganizationLevelIds: number[];
}

interface ActivitySelectionGroup {
  kind: MinisterialActivityKind;
  label: string;
  options: ActivitySelectionOption[];
}

interface ActivitySelectionOption {
  kind: MinisterialActivityKind;
  id: number;
  name: string;
  isActive: boolean;
}

interface MinisterialActionDraft {
  values: ReturnType<PersonMinistryEditorFormComponent['form']['getRawValue']>;
  designations: FormArray<FormGroup>;
  controls: Array<{
    control: AbstractControl;
    validator: AbstractControl['validator'];
    asyncValidator: AbstractControl['asyncValidator'];
    errors: AbstractControl['errors'];
    disabled: boolean;
    dirty: boolean;
    touched: boolean;
  }>;
  appointmentEndSelectionKeys: string[];
  officiantAreaEditing: boolean;
}

interface ActivitySummaryItem {
  kind: MinisterialActivityKind;
  referenceId: number;
  referenceName: string;
  organizationUnitId?: string;
  organizationUnitName: string;
  historicalOrganizationUnitName?: string;
}

interface ReassignmentPayloadItem {
  kind: MinisterialActivityKind;
  referenceId: number;
  fromOrganizationUnitId?: string | null;
  fromHistoricalOrganizationUnitName?: string | null;
  toOrganizationUnitId?: string | null;
  toHistoricalOrganizationUnitName?: string | null;
}

interface EffectiveMinistryState {
  ministryId: number;
  ministryName: string;
  status: string;
}

interface LicenseContext {
  startDate: string;
  endDate?: string;
  ministryName?: string;
}

const REGIONAL_CHURCH_LEVEL_ID = 1;
const DISTRICT_APOSTLE_APPOINTMENT_NAME = 'apostol de distrito';

@Component({
  selector: 'app-person-ministry-editor-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatTooltipModule,
    DatePickerComponent,
    UnitSelectorComponent,
  ],
  templateUrl: './person-ministry-editor-form.component.html',
  styleUrls: [
    './person-ministry-editor-form.component.scss',
    './person-ministry-editor-form.mobile.scss',
    './person-ministry-editor-form.fields.scss',
  ],
})
export class PersonMinistryEditorFormComponent implements OnInit {
  @Input({ required: true }) person!: Person;
  readonly designVersion = 'ministerial';
  @Input() mode: 'correction' | 'change' = 'change';
  @Input() ministries: ReferenceItem[] = [];
  @Input() appointments: ReferenceItem[] = [];
  @Input() assignments: ReferenceItem[] = [];
  @Input() units: OrganizationUnitItem[] = [];
  @Input() currentAreas: MinisterialActivityArea[] = [];
  @Input() currentEvent: PersonEvent | null = null;
  @Input() ministryEvents: PersonEvent[] = [];
  @Input() appointmentEvents: PersonEvent[] = [];
  @Input() assignmentEvents: PersonEvent[] = [];
  private membershipUnitSuggestions: OrganizationUnitItem[] = [];

  get suggestedActivityUnits(): OrganizationUnitItem[] {
    const id = this.person?.communityId;
    const community = this.units.find(unit => unit.id.toLowerCase() === id?.toLowerCase());
    const name = community?.name ?? this.person?.communityName;
    if (!id || !name) {
      if (this.membershipUnitSuggestions.length) this.membershipUnitSuggestions = [];
    } else if (this.membershipUnitSuggestions[0]?.id !== id || this.membershipUnitSuggestions[0]?.name !== name) {
      this.membershipUnitSuggestions = [community ?? { id, name, levelId: 5, levelName: 'Comunidad' }];
    }
    return this.membershipUnitSuggestions;
  }
  @Input() set canManageFullMinisterialActivity(value: boolean) {
    this.canManageFullMinisterialActivityValue = value;
    this.rebuildMinisterialActionOptions();
  }

  @Output() cancelled = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  readonly isSaving = signal(false);
  officiantAreaEditing = false;
  readonly noAllowedLevelIds: number[] = [];
  readonly kindOptions: Array<{ value: MinisterialActivityKind; label: string }> = [
    { value: 'Ministry', label: 'Ministerio' },
    { value: 'Appointment', label: 'Encargo' },
    { value: 'Assignment', label: 'Nombramiento' },
  ];

  readonly form;
  private readonly unitNames = new Map<string, string>();
  private readonly unitLevelIds = new Map<string, number>();
  private readonly knownUnits = new Map<string, OrganizationUnitItem>();
  private readonly designationGroupCache = new Map<number, {
    key: string;
    groups: ActivitySelectionGroup[];
  }>();
  private effectiveStateFromApi: MinisterialActivityEffectiveState | null = null;
  private effectiveStateDate: string | null = null;
  private correctionReassignmentSeeded = false;
  private transitionPreview: MinisterialActivityTransitionPreview | null = null;
  private readonly appointmentEndSelectionKeys = new Set<string>();
  previewLoading = false;
  previewUnavailable = false;
  previewErrorMessage: string | null = null;
  effectiveStateLoading = false;
  effectiveStateUnavailable = false;
  actionDefinitions: MinisterialActivityActionDefinition[] = [];
  ministerialActionOptions: MinisterialActionOption[] = [];
  ministerialMobileStep: MinisterialMobileStep = 'action';
  isMinisterialMobileLayout = false;
  readonly actionFilterControl = new FormControl('', { nonNullable: true });
  private canManageFullMinisterialActivityValue = false;
  private membershipDialogOpen = false;
  private readonly collapsedDesignations = new WeakSet<FormGroup>();
  private readonly actionDrafts = new Map<MinistryEventType, MinisterialActionDraft>();
  private activeDraftAction: MinistryEventType | null = null;
  private preserveDraftOnNextState = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly personService: PersonService,
    private readonly organizationUnitService: OrganizationUnitService,
    private readonly notifications: NotificationService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
  ) {
    this.form = this.fb.group({
      action: [null as MinistryEventType | null, Validators.required],
      eventDate: [null as any, Validators.required],
      membershipCommunityId: [null as string | null],
      institutionAreaIds: [[] as string[], Validators.minLength(1)],
      expectedEndDate: [null as any],
      primaryOrganizationUnitId: [null as string | null],
      primaryAreaMode: ['unit' as 'unit' | 'historical'],
      primaryHistoricalOrganizationUnitName: ['', Validators.maxLength(200)],
      eventActPlace: [null as string | null],
      actPlaceMode: ['community' as ActPlaceMode],
      actOrganizationUnitId: [null as string | null],
      actLocation: ['', Validators.maxLength(200)],
      reason: ['', Validators.maxLength(500)],
      designations: this.fb.array<FormGroup>([]),
    });
  }

  ngOnInit(): void {
    this.updateMinisterialMobileLayout();
    this.registerKnownUnits();
    this.organizationUnitService.getAllCommunities().subscribe({
      next: communities => {
        for (const community of communities) {
          this.registerKnownUnit({
            id: community.id,
            name: community.name,
            levelId: community.levelId ?? community.organizationLevelId,
            levelName: community.levelName ?? 'Comunidad',
            parentId: community.parentUnitId,
            ancestorNames: community.ancestorNames ?? [],
          });
        }
        this.syncInstitutionMembershipCommunity();
        this.cdr.markForCheck();
      },
      error: () => this.notifications.error('No se pudieron cargar las comunidades para elegir la pertenencia.'),
    });
    this.personService.getMinisterialActivityActions().subscribe({
      next: definitions => {
        this.actionDefinitions = definitions;
        this.rebuildMinisterialActionOptions();
        this.cdr.markForCheck();
      },
      error: () => this.notifications.error('No se pudieron cargar las acciones ministeriales.'),
    });
    this.form.controls.action.valueChanges.subscribe(action => {
      if (action === this.activeDraftAction) return;
      this.switchActionDraft(action);
      this.ministerialMobileStep = action ? 'form' : 'action';
      this.form.controls.eventDate.setValue(this.form.controls.eventDate.value);
    });
    this.form.controls.actPlaceMode.valueChanges.subscribe(() => this.syncActPlaceValidation());
    this.form.controls.eventDate.valueChanges.pipe(
      map(value => {
        this.presetLicenseEndDate();
        if (!value) {
          return null;
        }

        try {
          return this.toIsoDate(value);
        } catch {
          return null;
        }
      }),
      switchMap(effectiveDate => {
        this.designations.controls.forEach(group => group.controls['designationEndDate']?.updateValueAndValidity({ emitEvent: false }));
        const preserveDraft = this.preserveDraftOnNextState;
        this.preserveDraftOnNextState = false;
        this.effectiveStateDate = effectiveDate;
        this.effectiveStateFromApi = null;
        this.effectiveStateUnavailable = false;
        if (!effectiveDate || !this.selectedAction) {
          this.effectiveStateLoading = false;
          return of({ state: null, preserveDraft });
        }

        this.effectiveStateLoading = true;
        return this.personService.getMinisterialActivityEffectiveState(
          this.person.id,
          effectiveDate,
          this.currentEvent?.id,
        ).pipe(
          catchError(() => {
            this.effectiveStateUnavailable = true;
            return of(null);
          }),
          map(state => ({ state, preserveDraft })),
        );
      }),
    ).subscribe(({ state, preserveDraft }) => {
      this.effectiveStateLoading = false;
      this.effectiveStateFromApi = state;
      for (const designation of this.safeEffectiveDesignations(state)) {
        if (designation.organizationUnitId) {
          this.unitNames.set(designation.organizationUnitId, designation.organizationUnitName);
        }
      }
      if (!preserveDraft && state && this.isReassignment && (!this.currentEvent || !this.correctionReassignmentSeeded)) {
        this.reseedReassignmentDesignation();
        this.correctionReassignmentSeeded = !!this.currentEvent;
      }
      if (!preserveDraft && !this.currentEvent && this.isReinstatement) {
        this.reseedReinstatementDesignation();
      }
      this.cdr.markForCheck();
    });
    if (this.currentEvent) {
      this.initializeCorrection();
      return;
    }

    this.form.patchValue({ eventDate: this.dateAdapter.today() });
  }

  get designations(): FormArray<FormGroup> {
    return this.form.controls.designations;
  }

  get selectedAction(): MinistryEventType | null {
    return this.form.controls.action.value;
  }

  get isHistoricalPrimaryArea(): boolean {
    return this.form.controls.primaryAreaMode.value === 'historical';
  }

  get selectedActionOption(): MinisterialActionOption | undefined {
    return this.allAvailableActions.find(action => action.value === this.selectedAction);
  }

  get availableActions(): MinisterialActionOption[] {
    const actions = this.allAvailableActions;
    const filter = this.normalizedActionFilter;
    if (!filter) {
      return actions;
    }

    return actions.filter(action =>
      this.normalizeActionFilterText(`${action.label} ${action.description}`).includes(filter));
  }

  private get allAvailableActions(): MinisterialActionOption[] {
    return this.ministerialActionOptions;
  }

  private rebuildMinisterialActionOptions(): void {
    const editingOrdination = this.currentEvent?.eventTypeKey === 'Ordination';
    const definitions = [...this.actionDefinitions];
    if (editingOrdination && !definitions.some(definition => definition.eventType === 'Ordination')) {
      definitions.push({
        eventType: 'Ordination', requiresEffectiveDate: true, requiresActPlace: false,
        requiresComment: false, requiresMinistry: true, requiresAppointmentOrAssignment: false,
        requiresArea: true, requiresExpectedEndDate: false, targetStatus: 'Active',
      });
    }
    this.ministerialActionOptions = definitions
      .filter(definition =>
        (!hiddenMinisterialActivityActions.has(definition.eventType) || definition.eventType === this.currentEvent?.eventTypeKey) &&
        (definition.eventType !== 'Ordination' || editingOrdination) &&
        (!editingOrdination || definition.eventType !== 'MinisterialInstitution') &&
        (this.canManageFullMinisterialActivityValue || definition.eventType === 'NewOfficiant'))
      .map(definition => {
        const presentation = actionPresentationFor(editingOrdination && definition.eventType === 'Ordination'
          ? { ...definition, eventType: 'MinisterialInstitution' } : definition);
        return {
          value: definition.eventType,
          label: presentation.label,
          description: presentation.description,
          icon: presentation.icon,
          group: presentation.group,
        };
      })
      .sort((left, right) => compareMinisterialActivityActions(
        left.value === 'Ordination' ? 'MinisterialInstitution' : left.value,
        right.value === 'Ordination' ? 'MinisterialInstitution' : right.value));
  }

  private get normalizedActionFilter(): string {
    return this.normalizeActionFilterText(this.actionFilterControl.value);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateMinisterialMobileLayout();
  }

  get isOrdination(): boolean {
    return this.selectedAction === 'Ordination';
  }

  get isReassignment(): boolean {
    return this.selectedAction === 'Reassignment';
  }

  get isReinstatement(): boolean {
    return this.selectedAction === 'Reinstatement';
  }

  get isNewOfficiantAction(): boolean {
    return this.selectedAction === 'NewOfficiant';
  }

  get isInstitutionAction(): boolean {
    return this.selectedAction === 'MinisterialInstitution' ||
      (this.currentEvent?.eventTypeKey === 'Ordination' && this.selectedAction === 'Ordination');
  }

  get isAppointmentAssignmentAction(): boolean {
    return this.selectedAction === 'AppointmentAssignment';
  }

  get isAppointmentEndAction(): boolean {
    return this.selectedAction === 'AppointmentEnd';
  }

  get isSingleMinistryAssignmentAction(): boolean {
    return this.isInstitutionAction || this.selectedAction === 'Confirmation';
  }

  get isLicenseRenewalAction(): boolean {
    return this.selectedAction === 'MinisterialLicenseRenewal';
  }

  get showEventDetailsFields(): boolean {
    return !this.isLicenseRenewalAction && !this.shouldHideReinstatementFields;
  }

  get shouldHideReinstatementFields(): boolean {
    return this.isReinstatement && !this.isReinstatementDateValid;
  }

  get isReinstatementDateValid(): boolean {
    if (!this.isReinstatement) {
      return true;
    }

    return this.effectiveCurrentMinistry?.status === 'Licensed';
  }

  get reinstatementDateMessage(): string {
    if (this.effectiveStateLoading) {
      return 'Consultando si existe una licencia activa en la fecha seleccionada...';
    }

    if (this.effectiveStateUnavailable) {
      return 'No se pudo validar si existe una licencia activa en la fecha seleccionada.';
    }

    return 'Para registrar un reintegro, elegí una fecha en la que el ministerio esté de licencia.';
  }

  get isActivationEvent(): boolean {
    return this.isOrdination || this.isReinstatement;
  }

  trackActionOption(_: number, action: MinisterialActionOption): MinistryEventType {
    return action.value;
  }

  clearActionFilter(): void {
    this.actionFilterControl.setValue('');
  }

  get requiresDesignations(): boolean {
    if (this.isInstitutionAction) return true;
    if (this.isNewOfficiantAction) {
      return false;
    }

    const definition = this.selectedActionDefinition;
    if (definition) {
      return definition.requiresMinistry || definition.requiresAppointmentOrAssignment || definition.requiresArea;
    }

    return this.isActivationEvent || this.isReassignment;
  }

  get designationsRequired(): boolean {
    if (this.isInstitutionAction) return true;
    if (this.isNewOfficiantAction) {
      return false;
    }

    const definition = this.selectedActionDefinition;
    if (definition) {
      return definition.requiresMinistry || definition.requiresAppointmentOrAssignment || definition.requiresArea;
    }

    return this.isReinstatement ||
      this.isReassignment ||
      (this.isOrdination && this.effectiveCurrentMinistry?.status !== 'Active');
  }

  get showEventForm(): boolean {
    return !!this.selectedAction;
  }

  get actPlaceMode(): ActPlaceMode {
    return this.form.controls.actPlaceMode.value ?? 'none';
  }

  get eventDetailsSummaryLabel(): string {
    const actPlaceLabel = this.actPlaceLabel;
    return actPlaceLabel
      ? `${this.resultEffectiveDateLabel} · ${actPlaceLabel}`
      : this.resultEffectiveDateLabel;
  }

  get actPlaceLabel(): string {
    if (this.actPlaceMode === 'location') {
      return this.form.controls.actLocation.value?.trim() || '';
    }

    const unitId = this.form.controls.actOrganizationUnitId.value;
    if (unitId) {
      return this.unitName(unitId);
    }

    return '';
  }

  get submitLabel(): string {
    if (this.currentEvent) {
      return 'Guardar corrección';
    }

    return this.selectedActionOption
      ? `Registrar ${this.selectedActionOption.label.toLocaleLowerCase('es')}`
      : 'Registrar evento';
  }

  get selectedActionDefinition(): MinisterialActivityActionDefinition | undefined {
    return this.actionDefinitions.find(definition => definition.eventType === this.selectedAction);
  }

  get requiresExpectedEndDate(): boolean {
    return this.selectedActionDefinition?.requiresExpectedEndDate === true;
  }

  get isLicenseEndDateAction(): boolean {
    return this.selectedAction === 'LeaveOfAbsence' || this.selectedAction === 'MinisterialLicenseRenewal';
  }

  get currentLicenseContext(): LicenseContext | null {
    const referenceDate = this.selectedReferenceDate;
    if (!referenceDate || this.ministryStatusAtDate(referenceDate) !== 'Licensed') {
      return null;
    }

    let currentLicense: LicenseContext | null = null;

    for (const event of this.sortedMinistryEvents) {
      if (!event.eventDate || event.eventDate > referenceDate) {
        continue;
      }

      if (event.eventTypeKey === 'LeaveOfAbsence') {
        currentLicense = {
          startDate: event.eventDate,
          endDate: event.endDate,
          ministryName: event.ministryId === this.effectiveCurrentMinistry?.ministryId
            ? this.effectiveCurrentMinistry?.ministryName
            : event.detail ?? this.effectiveCurrentMinistry?.ministryName,
        };
        continue;
      }

      if (event.eventTypeKey === 'MinisterialLicenseRenewal' && currentLicense) {
        currentLicense = {
          ...currentLicense,
          endDate: event.endDate,
        };
      }

      if (event.eventTypeKey === 'Reinstatement') {
        currentLicense = null;
      }
    }

    return currentLicense;
  }

  formatIsoDateLabel(value?: string): string {
    if (!value) {
      return 'Sin informar';
    }

    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  }

  get requiresActPlace(): boolean {
    return this.selectedActionDefinition?.requiresActPlace === true;
  }

  get requiresOfficiantArea(): boolean {
    return this.isNewOfficiantAction;
  }

  get requiresComment(): boolean {
    return this.selectedActionDefinition?.requiresComment === true;
  }

  get designationFieldLabel(): string {
    return this.selectedActionDefinition
      ? actionPresentationFor(this.selectedActionDefinition).designationLabel ?? 'Designacion'
      : 'Designacion';
  }

  get canSubmit(): boolean {
    return !this.isSaving()
      && !this.isValidationPending
      && !!this.selectedAction
      && this.form.valid
      && !this.hasDuplicateActivityAreas()
      && !this.hasInvalidDesignationAreas()
      && !this.hasIncompleteDesignations();
  }

  get submitDisabled(): boolean {
    return this.isSaving() || this.isValidationPending || this.membershipDialogOpen;
  }

  get isValidationPending(): boolean {
    return this.effectiveStateLoading;
  }

  get submitButtonLabel(): string {
    if (this.isSaving()) {
      return 'Guardando...';
    }

    if (this.isValidationPending) {
      return 'Consultando...';
    }

    return this.submitLabel;
  }

  get primaryAreaName(): string {
    if (this.isHistoricalPrimaryArea) {
      return this.form.controls.primaryHistoricalOrganizationUnitName.value?.trim() || 'Sin seleccionar';
    }
    const unitId = this.form.controls.primaryOrganizationUnitId.value;
    return unitId ? this.unitName(unitId) : 'Sin seleccionar';
  }

  get currentReferences(): ActivityReference[] {
    const references = new Map<string, ActivityReference>();

    if (this.effectiveCurrentMinistry) {
      const ministry = this.effectiveCurrentMinistry;
      const option = this.getCatalogOption('Ministry', ministry.ministryId);
      references.set(this.referenceKey('Ministry', ministry.ministryId), {
        kind: 'Ministry',
        id: ministry.ministryId,
        name: ministry.ministryName,
        isActive: true,
        allowedOrganizationLevelIds: option?.allowedOrganizationLevelIds ?? this.noAllowedLevelIds,
      });
    }

    for (const area of this.effectiveCurrentAreas) {
      const key = this.referenceKey(area.kind, area.referenceId);
      if (!references.has(key)) {
        const option = this.getCatalogOption(area.kind, area.referenceId);
        references.set(key, {
          kind: area.kind,
          id: area.referenceId,
          name: area.referenceName,
          isActive: option?.isActive !== false,
          allowedOrganizationLevelIds: option?.allowedOrganizationLevelIds ?? this.noAllowedLevelIds,
        });
      }
    }
    return [...references.values()];
  }

  get reassignmentClosingDesignations(): ActivityReference[] {
    const resultKeys = new Set(this.designationSummaryItems.map(item => this.summaryKey(item)));
    const removedKinds = this.currentSummaryItems
      .filter(item => item.kind !== 'Ministry' && !resultKeys.has(this.summaryKey(item)))
      .map(item => this.referenceKey(item.kind, item.referenceId));

    return this.currentReferences.filter(reference =>
      reference.kind !== 'Ministry' &&
      removedKinds.includes(this.referenceKey(reference.kind, reference.id)));
  }

  get reassignmentClosingDesignationsLabel(): string {
    return this.reassignmentClosingDesignations.map(reference => reference.name).join(', ');
  }

  get reassignmentDesignation(): FormGroup | null {
    return this.designations.length > 0 ? this.designations.at(0) : null;
  }

  get reassignmentAreaCount(): number {
    return this.designations.length > 0 ? this.specificAreas(0).length : 0;
  }

  get currentSummaryItems(): ActivitySummaryItem[] {
    if (this.transitionPreview) {
      return this.mapPreviewDesignations(this.transitionPreview.before.designations);
    }

    return this.effectiveCurrentAreas.map(area => ({
      kind: area.kind,
      referenceId: area.referenceId,
      referenceName: area.referenceName,
      organizationUnitId: area.organizationUnitId,
      organizationUnitName: area.organizationUnitName,
      historicalOrganizationUnitName: area.isHistoricalOrganizationUnit ? area.organizationUnitName : undefined,
    }));
  }

  get resultSummaryItems(): ActivitySummaryItem[] {
    if (this.transitionPreview) {
      return this.mapPreviewDesignations(this.transitionPreview.after.designations);
    }

    return [];
  }

  get hasSimpleCurrentMinistrySummary(): boolean {
    return this.isSimpleMinistrySummary(this.currentSummaryItems, this.currentMinistryName);
  }

  get hasSimpleResultMinistrySummary(): boolean {
    return this.isSimpleMinistrySummary(this.visibleResultSummaryItems, this.resultMinistryName);
  }

  get currentMinistryAreaNames(): string {
    return this.summaryAreaNames(this.currentSummaryItems);
  }

  get resultMinistryAreaNames(): string {
    return this.summaryAreaNames(this.visibleResultSummaryItems);
  }

  get visibleResultSummaryItems(): ActivitySummaryItem[] {
    if (!this.ordinationMovesCurrentMinistry) {
      return this.resultSummaryItems;
    }

    return this.resultSummaryItems.filter(item =>
      item.kind !== 'Ministry' ||
      item.referenceName !== this.resultMinistryName);
  }

  get endedSummaryItems(): ActivitySummaryItem[] {
    if (!this.hasCompleteResultSummary) {
      return [];
    }

    const resultKeys = new Set(this.resultSummaryItems.map(item => this.summaryKey(item)));
    return this.currentSummaryItems.filter(item => !resultKeys.has(this.summaryKey(item)));
  }

  get hasCompleteResultSummary(): boolean {
    if (!this.transitionPreview) {
      return false;
    }
    if (this.hasIncompleteDesignations()) {
      return false;
    }

    if (this.isReinstatement && !this.designationSummaryItems.some(item => item.kind === 'Ministry')) {
      return false;
    }

    return true;
  }

  get retainedRelatedItems(): ActivitySummaryItem[] {
    if (!this.isOrdination || !this.isChangingMinistry) {
      return [];
    }

    return this.currentSummaryItems.filter(item => item.kind !== 'Ministry');
  }

  private get reinstatementQuickAreas(): MinisterialActivityArea[] {
    return this.currentLicenseAreas;
  }

  private get currentLicenseAreas(): MinisterialActivityArea[] {
    const referenceDate = this.selectedReferenceDate;
    if (!referenceDate) {
      return [];
    }

    const ministryAreas = [...this.sortedMinistryEvents]
      .filter(event => event.eventTypeKey === 'LeaveOfAbsence' && !!event.eventDate && event.eventDate <= referenceDate)
      .sort((left, right) =>
        (right.eventDate ?? '').localeCompare(left.eventDate ?? '') ||
        right.id - left.id)
      .flatMap(event => event.ministerialAreas ?? [])
      .filter(area => area.kind === 'Ministry')
      .filter(area => area.changeType === 'Ended' || area.changeType === 'Added')
      .sort((left, right) =>
        (right.validFrom ?? '').localeCompare(left.validFrom ?? '') ||
        right.recordId - left.recordId);

    return ministryAreas.length > 0 ? [ministryAreas[0]] : [];
  }

  get effectiveCurrentMinistry(): EffectiveMinistryState | null {
    const serverState = this.serverEffectiveStateForSelectedDate;
    if (serverState) {
      return serverState.ministryId && serverState.ministryName && serverState.status
        ? {
            ministryId: serverState.ministryId,
            ministryName: serverState.ministryName,
            status: serverState.status,
          }
        : null;
    }

    return null;
  }

  private get effectiveCurrentAreas(): MinisterialActivityArea[] {
    const serverState = this.serverEffectiveStateForSelectedDate;
    if (serverState) {
      return this.safeEffectiveDesignations(serverState).map((designation, index) => ({
        ministryChangeId: 0,
        recordId: -(index + 1),
        kind: designation.kind,
        referenceId: designation.referenceId,
        referenceName: designation.referenceName,
        organizationUnitId: designation.organizationUnitId,
        organizationUnitName: designation.organizationUnitName,
        isHistoricalOrganizationUnit: designation.isHistoricalOrganizationUnit,
        changeType: 'Added',
        validFrom: serverState.effectiveDate,
      }));
    }

    return [];
  }

  get ordinationMovesCurrentMinistry(): boolean {
    if (!this.isOrdination || this.effectiveCurrentMinistry?.status !== 'Active') {
      return false;
    }

    const hasSelectedMinistry = this.designations.controls.some(group =>
      group.controls['kind'].value === 'Ministry' &&
      !!group.controls['referenceId'].value);
    const hasSelectedRelatedDesignation = this.designations.controls.some(group =>
      group.controls['kind'].value !== 'Ministry' &&
      !!group.controls['referenceId'].value);

    return !hasSelectedMinistry && hasSelectedRelatedDesignation;
  }

  get isChangingMinistry(): boolean {
    const target = this.designationSummaryItems.find(item => item.kind === 'Ministry');
    return !!target && target.referenceId !== this.effectiveCurrentMinistry?.ministryId;
  }

  get currentMinistryName(): string {
    if (this.transitionPreview) {
      return this.transitionPreview.before.ministryName ?? 'Sin ministerio';
    }
    return this.effectiveCurrentMinistry?.ministryName ?? 'Sin ministerio';
  }

  get resultMinistryName(): string {
    if (this.transitionPreview) {
      return this.transitionPreview.after.ministryName ?? 'Sin ministerio';
    }
    if (this.hasIncompleteDesignations()) {
      return 'Pendiente de seleccionar';
    }

    const selectedMinistry = this.designationSummaryItems.find(item => item.kind === 'Ministry');
    if (selectedMinistry) {
      return selectedMinistry.referenceName;
    }

    return this.isReinstatement ? 'Pendiente de seleccionar' : this.currentMinistryName;
  }

  get currentStatusLabel(): string {
    if (this.transitionPreview) {
      return this.statusLabel(this.transitionPreview.before.status ?? undefined);
    }
    return this.statusLabel(this.effectiveCurrentMinistry?.status);
  }

  get resultStatusLabel(): string {
    if (this.transitionPreview) {
      return this.statusLabel(this.transitionPreview.after.status ?? undefined);
    }
    if (this.hasIncompleteDesignations()) {
      return 'Pendiente';
    }

    if (this.isReinstatement && !this.designationSummaryItems.some(item => item.kind === 'Ministry')) {
      return 'Pendiente';
    }

    switch (this.selectedAction) {
      case 'Ordination': return 'En actividad';
      case 'Reinstatement': return 'En actividad';
      case 'LeaveOfAbsence': return 'De licencia';
      case 'Retirement': return 'En descanso';
      case 'Dismissal':
      case 'Resignation': return 'Finalizado';
      default: return this.currentStatusLabel;
    }
  }

  get resultEffectiveDateLabel(): string {
    const referenceDate = this.selectedReferenceDate;
    if (!referenceDate) {
      return 'Sin fecha';
    }

    const date = new Date(`${referenceDate}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return referenceDate;
    }

    return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  }

  get resultSummaryEmptyMessage(): string {
    if (this.previewLoading) {
      return 'Calculando la situaciÃ³n resultante...';
    }

    if (this.previewUnavailable) {
      return 'No se pudo validar la situaciÃ³n resultante. RevisÃ¡ los datos o intentÃ¡ nuevamente.';
    }

    if (!this.transitionPreview) {
      return 'CompletÃ¡ el evento para obtener la situaciÃ³n resultante validada.';
    }

    if (this.hasIncompleteDesignations()) {
      return 'Completá las designaciones para visualizar la situación resultante.';
    }

    if (this.isReinstatement && !this.designationSummaryItems.some(item => item.kind === 'Ministry')) {
      return 'Seleccioná el ministerio que se reactivará.';
    }

    if (this.selectedAction === 'LeaveOfAbsence') {
      return 'El ministerio queda de licencia; las áreas de actividad vigentes quedan suspendidas.';
    }

    return 'El evento finalizará las áreas de actividad vigentes.';
  }

  get endedSummaryTitle(): string {
    return this.selectedAction === 'LeaveOfAbsence'
      ? 'Se suspenden'
      : 'Finalizan';
  }

  get ministerialRegistrationSummaryText(): string {
    if (this.isReassignment) {
      const reassignments = this.reassignmentPayloadChanges();
      if (reassignments.length === 0) {
        return 'Se mantiene el ministerio actual. Elegí un área vigente y tocá "Hacer pase" para modificarla.';
      }
    }

    if (this.isReinstatement && !this.isReinstatementDateValid) {
      return this.reinstatementDateMessage;
    }

    if (!this.selectedAction || !this.form.valid || this.hasIncompleteDesignations() || this.hasInvalidDesignationAreas()) {
      return ministerialActivitySummaryConfig.incompleteMessage;
    }

    if (this.selectedAction === 'AppointmentAssignment') {
      return this.ministerialAppointmentAssignmentSummaryText;
    }

    const place = this.actPlaceLabel;
    const comment = this.form.controls.reason.value?.trim();
    const expectedEndDate = this.form.controls.expectedEndDate.value;
    const context = this.ministerialSummaryContext(this.requiresDesignations ? this.ministerialDesignationSummaryText : '', {
      expectedEndDateSentence: this.requiresExpectedEndDate || this.isLicenseEndDateAction
        ? expectedEndDate
          ? applyMinisterialActivitySummaryTemplate(
              ministerialActivitySummaryConfig.expectedEndDateSentence,
              this.ministerialSummaryContext('', { expectedEndDate: this.formatDateControlValue(expectedEndDate) }),
            )
          : this.requiresExpectedEndDate ? ministerialActivitySummaryConfig.expectedEndDateMissingSentence : ''
        : '',
      actPlaceSentence: place
        ? applyMinisterialActivitySummaryTemplate(
            ministerialActivitySummaryConfig.actPlaceSentence,
            this.ministerialSummaryContext('', { actPlace: place }),
          )
        : '',
      commentSentence: comment ? ministerialActivitySummaryConfig.commentSentence : '',
      designationSentence: this.requiresDesignations
        ? applyMinisterialActivitySummaryTemplate(
            ministerialActivitySummaryConfig.designationSentence,
            this.ministerialSummaryContext(this.ministerialDesignationSummaryText),
          )
        : '',
    });

    return applyMinisterialActivitySummaryTemplate(
      ministerialActivitySummaryConfig.actionTemplates[this.selectedAction] ??
        ministerialActivitySummaryConfig.default,
      context,
    );
  }

  private get ministerialAppointmentAssignmentSummaryText(): string {
    const items = this.designationSummaryItems;
    if (!items.length) {
      return applyMinisterialActivitySummaryTemplate(
        ministerialActivitySummaryConfig.appointmentAssignmentIncomplete,
        this.ministerialSummaryContext(),
      );
    }

    const descriptions = items.map(item =>
      `${this.appointmentAssignmentArticle(item.kind)} ${item.referenceName} para ${item.organizationUnitName}`);

    return applyMinisterialActivitySummaryTemplate(
      ministerialActivitySummaryConfig.appointmentAssignment,
      this.ministerialSummaryContext(this.joinNaturalList(descriptions)),
    );
  }

  private appointmentAssignmentArticle(kind: MinisterialActivityKind): string {
    return ministerialActivitySummaryConfig.activityArticles[kind];
  }

  private get ministerialDesignationSummaryText(): string {
    const items = this.designationSummaryItems;
    if (!items.length) {
      return ministerialActivitySummaryConfig.designationIncomplete;
    }

    const labels = items
      .slice(0, 3)
      .map(item => `${item.referenceName} en ${item.organizationUnitName}`);
    const remaining = items.length - labels.length;
    return applyMinisterialActivitySummaryTemplate(
      remaining > 0
        ? ministerialActivitySummaryConfig.designationListWithRemaining
        : ministerialActivitySummaryConfig.designationList,
      this.ministerialSummaryContext('', { items: labels.join(', '), remaining: remaining.toString() }),
    );
  }

  private ministerialSummaryContext(
    designationSummary = '',
    overrides: Partial<MinisterialActivitySummaryContext> = {},
  ): MinisterialActivitySummaryContext {
    return {
      actionLabel: this.selectedActionOption?.label?.toLocaleLowerCase('es') ?? 'evento ministerial',
      effectiveDate: this.resultEffectiveDateLabel,
      expectedEndDate: '',
      actPlace: this.actPlaceLabel,
      designationSummary,
      expectedEndDateSentence: '',
      actPlaceSentence: '',
      commentSentence: '',
      designationSentence: '',
      items: '',
      remaining: '',
      ...overrides,
    };
  }

  private formatDateControlValue(value: any): string {
    try {
      const isoDate = this.toIsoDate(value);
      const date = new Date(`${isoDate}T00:00:00`);
      return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
    } catch {
      return 'Sin fecha';
    }
  }

  private joinNaturalList(items: string[]): string {
    if (items.length <= 1) {
      return items[0] ?? '';
    }

    if (items.length === 2) {
      return `${items[0]} y ${items[1]}`;
    }

    return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
  }

  selectAction(action: MinistryEventType): void {
    if (this.currentEvent && action !== this.currentEvent.eventTypeKey) {
      return;
    }

    const disabledReason = this.actionDisabledReason(action);
    if (disabledReason) {
      this.notifications.error(disabledReason);
      return;
    }

    this.form.controls.action.setValue(action);
  }

  onBackToActions(): void {
    this.form.controls.action.reset();
    this.ministerialMobileStep = 'action';
  }

  onActPlaceModeChanged(mode: ActPlaceMode): void {
    if (this.requiresOfficiantArea && mode !== 'community') {
      return;
    }

    if (this.form.controls.actPlaceMode.value !== mode) {
      this.form.controls.actPlaceMode.setValue(mode);
    } else {
      this.syncActPlaceValidation();
    }
  }

  onEventActPlaceChanged(selection: UnitSelectorSelection | null): void {
    this.form.patchValue({
      actPlaceMode: selection?.kind === 'unit' ? 'community' : selection?.kind === 'text' ? 'location' : 'none',
      actOrganizationUnitId: selection?.kind === 'unit' ? selection.unit.id : null,
      actLocation: selection?.kind === 'text' ? selection.text.trim() : '',
    }, { emitEvent: false });
    this.syncActPlaceValidation();
    if (selection?.kind === 'unit') this.onActOrganizationUnitChanged(selection.unit);
    if (selection?.kind === 'query') {
      this.form.controls.eventActPlace.setValue(selection.text, { emitEvent: false, emitModelToViewChange: false });
      this.form.controls.eventActPlace.setErrors({
        ...this.form.controls.eventActPlace.errors, unconfirmedPlace: true,
      });
    } else {
      this.form.controls.eventActPlace.updateValueAndValidity({ emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  goToMinisterialMobileStep(step: MinisterialMobileStep): void {
    if (step !== 'action' && !this.showEventForm) {
      this.ministerialMobileStep = 'action';
      return;
    }

    this.ministerialMobileStep = step;
  }

  isActionDisabled(action: MinistryEventType): boolean {
    return !!this.actionDisabledReason(action);
  }

  actionDisabledReason(action: MinistryEventType): string {
    switch (action) {
      case 'AppointmentAssignment':
        return this.hasCurrentStatus('Active')
          ? ''
          : 'Disponible si hay un ministerio activo.';
      case 'MinisterialActivityExtension':
        return this.hasCurrentStatus('Active')
          ? ''
          : 'Disponible si hay un ministerio activo.';
      case 'Reassignment':
        return this.hasAnyActivePeriodWithAreas
          ? ''
          : 'Disponible si hay un período con ministerio en actividad.';
      case 'Reinstatement':
        return this.hasAnyStatusPeriod('Licensed')
          ? ''
          : 'Disponible si hay un período con ministerio de licencia.';
      case 'MinisterialLicenseRenewal':
        return this.hasAnyStatusPeriod('Licensed')
          ? ''
          : 'Disponible si hay una licencia ministerial vigente.';
      case 'AppointmentEnd':
        return this.hasCurrentActiveAppointmentOrAssignment
          ? ''
          : 'Disponible si hay un encargo o nombramiento activo.';
      case 'LeaveOfAbsence':
        return this.hasAnyStatusPeriod('Active')
          ? ''
          : 'Disponible si hay un período con ministerio en actividad.';
      case 'Retirement':
        return this.hasAnyStatusPeriod('Active', 'Licensed')
          ? ''
          : 'Disponible si hay un período con ministerio en actividad o de licencia.';
      case 'Dismissal':
      case 'Resignation':
        return this.hasAnyStatusPeriod('Active', 'Licensed', 'Retired')
          ? ''
          : 'Disponible si hay un período con ministerio vigente.';
      default:
        return '';
    }
  }

  private get hasAnyActivePeriodWithAreas(): boolean {
    return this.hasCurrentStatusWithAreas('Active') ||
      this.sortedMinistryEvents.some(event =>
        event.ministryStatus === 'Active' &&
        (event.ministerialAreas ?? []).some(area => area.changeType === 'Added'));
  }

  private hasAnyStatusPeriod(...statuses: string[]): boolean {
    return this.hasCurrentPersonStatus(...statuses) ||
      (!!this.effectiveCurrentMinistry?.status && statuses.includes(this.effectiveCurrentMinistry.status)) ||
      this.sortedMinistryEvents.some(event => !!event.ministryStatus && statuses.includes(event.ministryStatus));
  }

  private hasCurrentStatus(status: string): boolean {
    return this.effectiveCurrentMinistry?.status === status || this.person.ministry?.status === status;
  }

  private hasCurrentStatusWithAreas(status: string): boolean {
    return this.effectiveCurrentMinistry?.status === status && this.effectiveCurrentAreas.length > 0;
  }

  private hasCurrentPersonStatus(...statuses: string[]): boolean {
    return !!this.person.ministry?.status && statuses.includes(this.person.ministry.status);
  }

  private get hasCurrentActiveAppointmentOrAssignment(): boolean {
    return (this.person.appointments?.length ?? 0) > 0 ||
      (this.person.assignments?.length ?? 0) > 0 ||
      this.effectiveCurrentAreas.some(area =>
        area.kind === 'Appointment' || area.kind === 'Assignment');
  }

  get activeAppointableDesignations(): MinisterialActivityEffectiveDesignation[] {
    return this.effectiveCurrentAreas.filter(area =>
      area.kind === 'Appointment' || area.kind === 'Assignment');
  }

  isDesignationSelectedToEnd(designation: MinisterialActivityEffectiveDesignation): boolean {
    return this.appointmentEndSelectionKeys.has(this.appointmentEndSelectionKey(designation));
  }

  toggleDesignationToEnd(designation: MinisterialActivityEffectiveDesignation, selected: boolean): void {
    const selectionKey = this.appointmentEndSelectionKey(designation);
    const index = this.designations.controls.findIndex(group =>
      group.controls['appointmentEndSelectionKey']?.value === selectionKey);

    if (selected && index < 0) {
      const group = this.createDesignationGroup(
        designation.kind,
        designation.referenceId,
        true,
        [{
          organizationUnitId: designation.organizationUnitId ?? null,
          historicalOrganizationUnitName: designation.isHistoricalOrganizationUnit
            ? designation.organizationUnitName
            : null,
        }],
        false,
      );
      group.addControl('appointmentEndSelectionKey', new FormControl(selectionKey), { emitEvent: false });
      this.designations.push(group, { emitEvent: false });
      this.appointmentEndSelectionKeys.add(selectionKey);
    }

    if (!selected && index >= 0) {
      this.designations.removeAt(index, { emitEvent: false });
    }

    if (!selected) {
      this.appointmentEndSelectionKeys.delete(selectionKey);
    }

    this.form.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  addDesignation(initial?: { kind: MinisterialActivityKind | null; referenceId: number | null }): void {
    if (this.isSingleMinistryAssignmentAction && this.designations.length >= 1) {
      this.notifications.error('La institución ministerial admite un solo ministerio. Agregá áreas si necesitás más cobertura.');
      return;
    }

    const defaultArea = this.defaultAreaForNewDesignation();
    if (this.isAppointmentAssignmentAction) {
      this.designations.controls.forEach((group, index) => {
        if (this.isDesignationComplete(index)) this.collapsedDesignations.add(group);
      });
    }
    this.designations.push(this.createDesignationGroup(
      initial?.kind ?? null,
      initial?.referenceId ?? null,
      false,
      defaultArea ? [defaultArea] : []));
  }

  removeDesignation(index: number): void {
    this.designations.removeAt(index);
    this.designationGroupCache.clear();
  }

  isDesignationComplete(index: number): boolean {
    return this.designations.at(index).valid && this.specificAreas(index).length > 0;
  }

  isDesignationExpanded(index: number): boolean {
    return !this.isAppointmentAssignmentAction || this.designations.length === 1
      || !this.isDesignationComplete(index)
      || !this.collapsedDesignations.has(this.designations.at(index));
  }

  toggleDesignationExpanded(index: number): void {
    const group = this.designations.at(index);
    if (this.isDesignationExpanded(index)) {
      if (this.isDesignationComplete(index)) this.collapsedDesignations.add(group);
    } else {
      this.collapsedDesignations.delete(group);
    }
  }

  expandDesignation(index: number): void {
    this.collapsedDesignations.delete(this.designations.at(index));
  }

  designationSummaryTitle(index: number): string {
    return this.designations.at(index).controls['referenceId'].value
      ? this.designationName(index)
      : `Encargo / nombramiento ${index + 1}`;
  }

  designationAreaSummary(index: number): string {
    return this.specificAreas(index).controls.map((_, areaIndex) => this.specificAreaName(index, areaIndex)).join(' · ');
  }

  designationKindOptions(index: number): Array<{ value: MinisterialActivityKind; label: string }> {
    const currentGroup = this.designations.at(index);
    const currentKind = currentGroup.controls['referenceId'].value
      ? currentGroup.controls['kind'].value as MinisterialActivityKind | null
      : null;
    const allowedKinds = this.isReassignment
      ? new Set(this.currentReferences.map(reference => reference.kind))
      : this.isSingleMinistryAssignmentAction
        ? new Set<MinisterialActivityKind>(['Ministry'])
      : this.selectedAction === 'AppointmentAssignment'
        ? new Set<MinisterialActivityKind>(['Appointment', 'Assignment'])
      : new Set<MinisterialActivityKind>(['Ministry', 'Appointment', 'Assignment']);

    return this.kindOptions.filter(option =>
      allowedKinds.has(option.value) &&
      (option.value !== 'Ministry' || !currentKind || currentKind === 'Ministry'));
  }

  designationActivityGroups(index: number): ActivitySelectionGroup[] {
    const allowedKinds = new Set(this.designationKindOptions(index).map(option => option.value));
    const controlValue = this.designations.at(index).controls['activitySearch'].value;
    const searchTerm = typeof controlValue === 'string' && !this.isActivitySelectionValue(controlValue)
      ? this.normalizeActivitySearch(controlValue)
      : '';
    const groups = this.kindOptions
      .filter(option => allowedKinds.has(option.value))
      .map(option => ({
        kind: option.value,
        label: option.label,
        options: this.activityOptionsForKind(option.value)
          .filter(activity => !searchTerm || this.normalizeActivitySearch(activity.name).includes(searchTerm)),
      }))
      .filter(group => group.options.length > 0);

    const cacheKey = `${searchTerm}|${groups
      .map(group => `${group.kind}:${group.options.map(option => `${option.id}:${option.isActive}`).join(',')}`)
      .join('|')}`;
    const cached = this.designationGroupCache.get(index);
    if (cached?.key === cacheKey) {
      return cached.groups;
    }

    this.designationGroupCache.set(index, { key: cacheKey, groups });
    return groups;
  }

  trackActivityGroup(_: number, group: ActivitySelectionGroup): MinisterialActivityKind {
    return group.kind;
  }

  trackActivityOption(_: number, option: ActivitySelectionOption): string {
    return `${option.kind}:${option.id}`;
  }

  displayActivitySelection = (selectionValue: string | null): string => {
    const selection = this.parseActivitySelectionValue(selectionValue);
    if (!selection) {
      return selectionValue ?? '';
    }

    return this.getCatalogOption(selection.kind, selection.referenceId)?.name
      ?? this.currentReferences.find(reference =>
        reference.kind === selection.kind && reference.id === selection.referenceId)?.name
      ?? '';
  };

  onDesignationActivityInput(index: number): void {
    const group = this.designations.at(index);
    const controlValue = group.controls['activitySearch'].value;
    const selectedValue = this.activitySelectionValue(
      group.controls['kind'].value as MinisterialActivityKind | null,
      group.controls['referenceId'].value as number | null,
    );

    if (controlValue === selectedValue) {
      return;
    }

    group.controls['kind'].reset();
    group.controls['referenceId'].reset();
    if (this.isAppointmentAssignmentAction || this.isReinstatement) {
      group.controls['selectedAreaIds'].setValue([]);
      group.controls['selectedAreaIds'].disable();
      this.specificAreas(index).clear();
    }
    this.designationGroupCache.delete(index);
  }

  onDesignationActivitySelected(index: number, selectionValue: string | null): void {
    if (!selectionValue) {
      return;
    }

    const [kindValue, referenceIdValue] = selectionValue.split(':');
    const kind = this.kindOptions.some(option => option.value === kindValue)
      ? kindValue as MinisterialActivityKind
      : null;
    const referenceId = Number(referenceIdValue);
    if (!kind || !Number.isInteger(referenceId)) {
      return;
    }

    const group = this.designations.at(index);
    group.controls['designationEditing']?.setValue(true);
    group.controls['kind'].setValue(kind);
    group.controls['referenceId'].setValue(referenceId);
    group.controls['activitySearch'].setValue(selectionValue, { emitEvent: false });
    if (this.isInstitutionAction) {
      this.specificAreas(index).clear();
      this.form.controls.institutionAreaIds.setValue([], { emitEvent: false });
      this.form.controls.institutionAreaIds.enable({ emitEvent: false });
      this.form.controls.membershipCommunityId.setValue(null, { emitEvent: false });
      this.syncInstitutionMembershipCommunity();
    } else if (this.isAppointmentAssignmentAction || this.isReinstatement) {
      this.specificAreas(index).clear();
      group.controls['selectedAreaIds'].setValidators([Validators.required, Validators.minLength(1)]);
      group.controls['selectedAreaIds'].setValue([]);
      group.controls['selectedAreaIds'].enable();
      this.form.controls.membershipCommunityId.setValue(null, { emitEvent: false });
    } else {
      this.syncAreasForSelectedDesignation(index);
    }
    this.designationGroupCache.clear();
  }

  usesDifferentArea(index: number): boolean {
    return !!this.designations.at(index).controls['usesDifferentArea'].value;
  }

  showExpandedAreas(index: number): boolean {
    return this.specificAreas(index).length > 1;
  }

  isDesignationEditing(index: number): boolean {
    return !!this.designations.at(index).controls['designationEditing']?.value;
  }

  modifyDesignation(index: number): void {
    this.designations.at(index).controls['designationEditing']?.setValue(true);
    this.modifyDesignationArea(index);
  }

  showInlineAreaEditor(index: number): boolean {
    return this.specificAreas(index).length === 1 && (
      this.isReassignment
        ? this.isSpecificAreaEditing(index, 0)
        : this.isReinstatement
          ? this.isSpecificAreaEditing(index, 0)
        : this.usesDifferentArea(index));
  }

  firstAreaName(index: number): string {
    const area = this.specificAreas(index).at(0);
    if (!area) {
      return 'Sin seleccionar';
    }

    const historicalName = area.controls['historicalOrganizationUnitName'].value?.trim();
    const organizationUnitId = area.controls['organizationUnitId'].value as string | null;
    const group = this.designations.at(index);
    const kind = group.controls['kind'].value as MinisterialActivityKind | null;
    const referenceId = group.controls['referenceId'].value as number | null;
    return historicalName || (
      organizationUnitId && this.isUnitAllowedForDesignation(organizationUnitId, kind, referenceId)
        ? this.unitName(organizationUnitId)
        : 'Sin seleccionar'
    );
  }

  canRemoveDesignation(index: number): boolean {
    if (this.isReassignment) {
      return false;
    }

    if (this.isSingleMinistryAssignmentAction) {
      return false;
    }

    return this.designations.length > 1;
  }

  useDifferentArea(index: number): void {
    const group = this.designations.at(index);
    group.controls['usesDifferentArea'].setValue(true);
    if (this.specificAreas(index).length === 0) {
      this.addSpecificArea(
        index,
        this.form.controls.primaryOrganizationUnitId.value,
        this.isHistoricalPrimaryArea
          ? this.form.controls.primaryHistoricalOrganizationUnitName.value?.trim() || null
          : null,
      );
    }
  }

  modifyDesignationArea(index: number): void {
    this.useDifferentArea(index);
    if (this.isReinstatement) {
      this.specificAreas(index).at(0)?.controls['isEditing']?.setValue(true);
    }
  }

  modifyOfficiantArea(): void {
    this.officiantAreaEditing = true;
  }

  usePrimaryArea(index: number): void {
    const group = this.designations.at(index);
    group.controls['usesDifferentArea'].setValue(false);
    this.specificAreas(index).clear();
  }

  specificAreas(index: number): FormArray<FormGroup> {
    return this.designations.at(index).controls['specificAreas'] as FormArray<FormGroup>;
  }

  addSpecificArea(
    index: number,
    organizationUnitId: string | null = null,
    historicalOrganizationUnitName: string | null = null,
  ): void {
    this.specificAreas(index).push(this.createSpecificAreaGroup(organizationUnitId, historicalOrganizationUnitName));
  }

  removeSpecificArea(designationIndex: number, areaIndex: number): void {
    this.specificAreas(designationIndex).removeAt(areaIndex);
  }

  isSpecificAreaEditing(designationIndex: number, areaIndex: number): boolean {
    return !!this.specificAreas(designationIndex).at(areaIndex)?.controls['isEditing']?.value;
  }

  startSpecificAreaPass(designationIndex: number, areaIndex: number): void {
    this.specificAreas(designationIndex).at(areaIndex)?.controls['isEditing']?.setValue(true);
  }

  reassignmentDestinationsControl(designationIndex: number, areaIndex: number): FormControl<string[]> {
    return this.specificAreas(designationIndex).at(areaIndex).controls['destinations'] as FormControl<string[]>;
  }

  onReassignmentDestinationsChanged(designationIndex: number, areaIndex: number, units: OrganizationUnitItem[]): void {
    units.forEach(unit => this.registerKnownUnit(unit));
    const area = this.specificAreas(designationIndex).at(areaIndex);
    const first = this.reassignmentDestinationAreas(area)[0];
    area.patchValue({
      areaMode: first?.historicalOrganizationUnitName ? 'historical' : 'unit',
      organizationUnitId: first?.organizationUnitId ?? null,
      historicalOrganizationUnitName: first?.historicalOrganizationUnitName ?? '',
    });
    this.onSpecificAreaModeChanged(designationIndex, areaIndex);
    this.form.controls.membershipCommunityId.setValue(null, { emitEvent: false });
    this.cdr.markForCheck();
  }

  private reassignmentDestinationAreas(area: FormGroup): Array<{
    organizationUnitId: string | null; historicalOrganizationUnitName: string | null;
  }> {
    return this.selectedUnitAreas(area.controls['destinations'].value as string[]);
  }

  private selectedUnitAreas(values: string[]): Array<{
    organizationUnitId: string | null; historicalOrganizationUnitName: string | null;
  }> {
    return values.map(value => {
      const isUnit = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
      return { organizationUnitId: isUnit ? value : null, historicalOrganizationUnitName: isUnit ? null : value.trim() };
    });
  }

  specificAreaName(designationIndex: number, areaIndex: number): string {
    const area = this.specificAreas(designationIndex).at(areaIndex);
    if (!area) {
      return 'Sin seleccionar';
    }

    const historicalName = area.controls['historicalOrganizationUnitName'].value?.trim();
    const organizationUnitId = area.controls['organizationUnitId'].value as string | null;
    return historicalName || (organizationUnitId ? this.unitName(organizationUnitId) : 'Sin seleccionar');
  }

  allowedLevelsForDesignation(index: number): number[] {
    const group = this.designations.at(index);
    return this.allowedLevelIdsForDesignation(
      group.controls['kind'].value as MinisterialActivityKind | null,
      group.controls['referenceId'].value as number | null);
  }

  usesMultipleUnitSelection(index: number): boolean {
    return this.isReinstatement || (this.isAppointmentAssignmentAction && !this.specificAreas(index).controls.some(area =>
      area.controls['areaMode'].value === 'historical'));
  }

  onDesignationUnitsChanged(index: number, units: OrganizationUnitItem[]): void {
    const areas = this.specificAreas(index);
    areas.clear();
    units.forEach(unit => this.registerKnownUnit(unit));
    const values = this.designations.at(index).controls['selectedAreaIds'].value as string[];
    for (const area of this.selectedUnitAreas(values)) {
      this.addSpecificArea(index, area.organizationUnitId, area.historicalOrganizationUnitName);
    }
    this.designations.at(index).controls['usesDifferentArea'].setValue(true);
    this.form.controls.membershipCommunityId.setValue(null, { emitEvent: false });
    this.cdr.markForCheck();
  }

  onPrimaryAreaChanged(unit: OrganizationUnitItem | null): void {
    if (unit) {
      this.registerKnownUnit(unit);
    }
  }

  onActOrganizationUnitChanged(unit: OrganizationUnitItem | null): void {
    if (unit) {
      this.unitNames.set(unit.id, unit.name);
      if (this.requiresOfficiantArea) {
        this.officiantAreaEditing = false;
      }
    }
  }

  onPrimaryAreaModeChanged(): void {
    const unitControl = this.form.controls.primaryOrganizationUnitId;
    const historicalControl = this.form.controls.primaryHistoricalOrganizationUnitName;
    if (this.isHistoricalPrimaryArea) {
      unitControl.setValue(null);
      unitControl.clearValidators();
      historicalControl.setValidators([Validators.required, Validators.maxLength(200)]);
    } else {
      historicalControl.setValue('');
      historicalControl.setValidators([Validators.maxLength(200)]);
      unitControl.setValidators(this.requiresDesignations ? [Validators.required] : []);
    }
    unitControl.updateValueAndValidity();
    historicalControl.updateValueAndValidity();
  }

  onSpecificAreaChanged(unit: OrganizationUnitItem | null): void {
    if (unit) {
      this.registerKnownUnit(unit);
    }
  }

  isHistoricalSpecificArea(designationIndex: number, areaIndex: number): boolean {
    return this.specificAreas(designationIndex).at(areaIndex).controls['areaMode'].value === 'historical';
  }

  onSpecificAreaModeChanged(designationIndex: number, areaIndex: number): void {
    const area = this.specificAreas(designationIndex).at(areaIndex);
    const unitControl = area.controls['organizationUnitId'];
    const historicalControl = area.controls['historicalOrganizationUnitName'];

    if (this.isHistoricalSpecificArea(designationIndex, areaIndex)) {
      unitControl.setValue(null);
      unitControl.clearValidators();
      historicalControl.setValidators([Validators.required, Validators.maxLength(200)]);
    } else {
      historicalControl.setValue('');
      historicalControl.setValidators([Validators.maxLength(200)]);
      unitControl.setValidators([Validators.required]);
    }

    unitControl.updateValueAndValidity();
    historicalControl.updateValueAndValidity();
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onSubmit(): void {
    if (this.isSaving() || this.membershipDialogOpen) return;
    if (!this.selectedAction) {
      this.notifications.error('Seleccioná el evento ministerial que querés registrar.');
      return;
    }

    if (this.isReinstatement && !this.isReinstatementDateValid) {
      this.notifications.error(this.reinstatementDateMessage);
      return;
    }

    if (this.designationsRequired && this.designations.length === 0) {
      this.notifications.error('Agregá al menos una designación.');
      return;
    }

    if (this.hasDuplicateActivityAreas()) {
      this.notifications.error('La misma actividad fue agregada más de una vez en la misma área.');
      return;
    }

    if (this.hasInvalidDesignationAreas()) {
      this.notifications.error('Una unidad seleccionada no pertenece a un nivel permitido para la designación.');
      return;
    }

    if (this.hasIncompleteDesignations()) {
      this.notifications.error('Completá la designación y su área de actividad antes de guardar.');
      return;
    }

    if ((this.isReinstatement || (this.isOrdination && this.effectiveCurrentMinistry?.status !== 'Active')) &&
        !this.designations.controls.some(group => group.controls['kind'].value === 'Ministry')) {
      this.notifications.error('Para volver a la actividad, el evento debe incluir el ministerio.');
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.notifications.error('Completá los datos requeridos antes de guardar.');
      return;
    }

    const raw = this.form.getRawValue();
    const areas = this.designationPayloadAreas();
    const effectiveDate = this.toIsoDate(raw.eventDate);
    const dateValidationMessage = this.actionDateValidationMessage(this.selectedAction, effectiveDate);
    if (dateValidationMessage) {
      this.notifications.error(dateValidationMessage);
      return;
    }

    if (this.isNewOfficiantAction) {
      this.saveOfficiant(raw, effectiveDate);
      return;
    }

    if (this.isValidationPending) {
      return;
    }

    const request = {
      eventType: this.selectedAction,
      effectiveDate,
      expectedEndDate: raw.expectedEndDate ? this.toIsoDate(raw.expectedEndDate) : undefined,
      actOrganizationUnitId: this.showEventDetailsFields && raw.actPlaceMode === 'community' ? raw.actOrganizationUnitId ?? undefined : undefined,
      actLocation: this.showEventDetailsFields && raw.actPlaceMode === 'location' ? raw.actLocation?.trim() || undefined : undefined,
      reason: this.showEventDetailsFields ? raw.reason?.trim() || undefined : undefined,
      membershipCommunityId: undefined as string | undefined,
      areas: this.isReassignment && !this.currentEvent ? [] : areas,
      reassignments: this.isReassignment && !this.currentEvent
        ? this.reassignmentPayloadChanges()
        : undefined,
    };

    if (this.isReassignment && !this.currentEvent && request.reassignments?.length === 0) {
      this.notifications.error('Seleccioná una designación y tocá "Hacer pase" para modificar su área.');
      return;
    }

    if (this.isAppointmentAssignmentAction) {
      this.confirmAppointmentMinistryAreas(request);
      return;
    }
    this.confirmMembershipAndSave(request, this.resolveMembershipCommunityOptions(areas));
  }

  private confirmAppointmentMinistryAreas(request: MinisterialActivityRequest): void {
    if (!this.effectiveCurrentMinistry || this.effectiveStateUnavailable) {
      this.notifications.error('No se pudo determinar la actividad ministerial vigente para esta fecha. Volvé a intentar.');
      return;
    }
    const currentAreas = this.effectiveCurrentAreas.filter(area => area.kind === 'Ministry');
    const key = (area: { organizationUnitId?: string | null; historicalOrganizationUnitName?: string | null }) =>
      `${area.organizationUnitId?.toLowerCase() ?? ''}|${area.historicalOrganizationUnitName?.trim() ?? ''}`;
    const destinations = new Set(request.areas.map(key));
    const currentKeys = new Set(currentAreas.map(area => key({
      organizationUnitId: area.organizationUnitId,
      historicalOrganizationUnitName: area.isHistoricalOrganizationUnit ? area.organizationUnitName : null,
    })));
    const differs = destinations.size !== currentKeys.size || [...destinations].some(value => !currentKeys.has(value));
    const continueRegistration = (keepCurrentMinistryAreas: boolean) => {
      const removed = new Set([...currentKeys].filter(value => !destinations.has(value)));
      const retained = this.effectiveCurrentAreas.filter(area => keepCurrentMinistryAreas ||
        (area.kind !== 'Ministry' && !removed.has(key({
          organizationUnitId: area.organizationUnitId,
          historicalOrganizationUnitName: area.isHistoricalOrganizationUnit ? area.organizationUnitName : null,
        }))));
      const communities = this.resolveMembershipCommunityOptions([...retained, ...request.areas], true);
      this.confirmMembershipAndSave({ ...request, keepCurrentMinistryAreas }, communities);
    };
    if (!differs) {
      continueRegistration(false);
      return;
    }
    this.membershipDialogOpen = true;
    this.dialog.open<AppointmentAreaDialogComponent, AppointmentAreaDialogData, boolean | null>(
      AppointmentAreaDialogComponent, modalDialogConfig({
        size: 'md', layout: 'form',
        data: {
          currentAreas: currentAreas.map(area => area.organizationUnitName),
          newAreas: [...new Set(request.areas.map(area => area.historicalOrganizationUnitName
            || (area.organizationUnitId ? this.unitName(area.organizationUnitId) : 'Área sin identificar')))],
        },
      }),
    ).afterClosed().subscribe(keep => {
      this.membershipDialogOpen = false;
      this.cdr.markForCheck();
      if (typeof keep === 'boolean') continueRegistration(keep);
    });
  }

  private confirmMembershipAndSave(request: MinisterialActivityRequest, membershipOptions: OrganizationUnitItem[]): void {
    const effectiveDate = request.effectiveDate;
    if (membershipOptions.length > 1) {
      this.membershipDialogOpen = true;
      this.dialog.open<MembershipCommunityDialogComponent, MembershipCommunityDialogData, string | null>(
        MembershipCommunityDialogComponent,
        modalDialogConfig({
          size: 'md', layout: 'form',
          data: { communities: membershipOptions, effectiveDateLabel: this.formatIsoDateLabel(effectiveDate) },
        }),
      ).afterClosed().subscribe(selectedId => {
        this.membershipDialogOpen = false;
        this.cdr.markForCheck();
        if (!selectedId || !membershipOptions.some(unit => unit.id === selectedId)) return;
        this.form.controls.membershipCommunityId.setValue(selectedId, { emitEvent: false });
        this.saveMinisterialActivity({ ...request, membershipCommunityId: selectedId });
      });
      return;
    }

    this.saveMinisterialActivity(request);
  }

  get membershipCommunityOptions(): OrganizationUnitItem[] {
    return this.resolveMembershipCommunityOptions(this.designationPayloadAreas());
  }

  get institutionMinistryId(): number | null {
    const group = this.designations.at(0);
    return group?.controls['kind'].value === 'Ministry'
      ? group.controls['referenceId'].value as number | null
      : null;
  }

  get institutionMinistryName(): string {
    return this.ministries.find(ministry => ministry.id === this.institutionMinistryId)?.name ?? '';
  }

  onInstitutionMinistryChanged(ministryId: number): void {
    this.onDesignationActivitySelected(0, `Ministry:${ministryId}`);
  }

  onInstitutionUnitsChanged(units: OrganizationUnitItem[]): void {
    for (const unit of units) {
      this.registerKnownUnit(unit);
    }
    this.onInstitutionAreasChanged(this.form.controls.institutionAreaIds.value ?? []);
  }

  onInstitutionAreasChanged(unitIds: string[]): void {
    const selectedUnitIds = unitIds ?? [];
    const areas = this.specificAreas(0);
    areas.clear();
    for (const selectedArea of this.selectedUnitAreas(selectedUnitIds)) {
      const unit = this.units.find(candidate => candidate.id === selectedArea.organizationUnitId);
      if (unit) {
        this.registerKnownUnit(unit);
      }
      this.addSpecificArea(0, selectedArea.organizationUnitId, selectedArea.historicalOrganizationUnitName);
    }
    this.designations.at(0).controls['usesDifferentArea'].setValue(selectedUnitIds.length > 0);
    this.syncInstitutionMembershipCommunity();
    this.form.controls.institutionAreaIds.markAsTouched();
    this.form.controls.institutionAreaIds.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  private syncInstitutionMembershipCommunity(): void {
    if (!this.isInstitutionAction) {
      return;
    }

    const options = this.membershipCommunityOptions;
    const selected = this.form.controls.membershipCommunityId.value;
    if (options.length === 1) {
      this.form.controls.membershipCommunityId.setValue(options[0].id, { emitEvent: false });
    } else if (!options.some(option => option.id === selected)) {
      this.form.controls.membershipCommunityId.setValue(null, { emitEvent: false });
    }
    this.form.controls.membershipCommunityId.clearValidators();
    this.form.controls.membershipCommunityId.updateValueAndValidity({ emitEvent: false });
  }

  private resolveMembershipCommunityOptions(
    areas: Array<{ organizationUnitId?: string | null }>,
    resultingAreasOnly = false,
  ): OrganizationUnitItem[] {
    const effectiveDateValue = this.form.controls.eventDate.value;
    const effectiveDate = effectiveDateValue ? this.toIsoDate(effectiveDateValue) : null;
    const isActiveOnEffectiveDate = (validFrom: string, validTo?: string | null) =>
      !effectiveDate || (validFrom <= effectiveDate && (!validTo || validTo >= effectiveDate));
    const candidateUnitIds = this.isInstitutionAction || resultingAreasOnly
      ? areas.map(area => area.organizationUnitId)
      : [
      ...this.effectiveCurrentAreas.map(area => area.organizationUnitId),
      ...this.currentAreas
        .filter(area => isActiveOnEffectiveDate(area.validFrom, area.validTo))
        .map(area => area.organizationUnitId),
      ...(this.person.ministry?.activities ?? [])
        .filter(activity => isActiveOnEffectiveDate(activity.validFrom, activity.validTo))
        .map(activity => activity.unitId),
      ...areas.map(area => area.organizationUnitId ?? undefined),
    ];
    const unitIds = new Set(candidateUnitIds.filter((id): id is string => !!id));
    const knownUnits = new Map(this.units.map(unit => [unit.id, unit]));
    for (const unit of this.knownUnits.values()) {
      knownUnits.set(unit.id, unit);
    }
    return [...unitIds]
      .map(id => knownUnits.get(id))
      .filter((unit): unit is OrganizationUnitItem => !!unit && unit.levelId === 5);
  }

  private saveMinisterialActivity(request: MinisterialActivityRequest): void {
    this.isSaving.set(true);
    const operation$: Observable<unknown> = this.currentEvent
      ? this.personService.updateMinisterialActivityEvent(this.person.id, this.currentEvent.id, request)
      : this.personService.createMinisterialActivityEvent(this.person.id, request);

    operation$.subscribe({
      next: () => {
        this.isSaving.set(false);
        if (this.currentEvent) {
          this.notifications.success('Actividad ministerial corregida correctamente.');
          this.saved.emit();
          return;
        }

        const retainedMessage = this.retainedRelatedItems.length > 0
          ? ` ${this.retainedRelatedItems.length} encargo(s) o nombramiento(s) continúan vigentes.`
          : '';
        this.notifications.success(`${this.selectedActionOption?.label ?? 'Actividad ministerial'} registrada correctamente.${retainedMessage}`);
        this.saved.emit();
      },
      error: (err: unknown) => {
        this.isSaving.set(false);
        this.notifications.error(getPersonEventEditorErrorMessage(
          err,
          this.currentEvent ? 'No se pudo corregir la actividad ministerial.' : 'No se pudo registrar la actividad ministerial.'));
      },
    });
  }

  private saveOfficiant(raw: {
    actPlaceMode: ActPlaceMode | null;
    actOrganizationUnitId: string | null;
    expectedEndDate: any;
    reason: string | null;
  }, effectiveDate: string): void {
    if (!raw.actOrganizationUnitId) {
      this.notifications.error('Seleccioná la comunidad donde se registra la actividad de oficiante.');
      this.form.controls.actOrganizationUnitId.markAsTouched();
      return;
    }

    this.isSaving.set(true);
    const request = {
      effectiveDate,
      endDate: this.currentEvent && raw.expectedEndDate ? this.toIsoDate(raw.expectedEndDate) : undefined,
      organizationUnitId: raw.actOrganizationUnitId,
      reason: raw.reason?.trim() || undefined,
    };
    const operation$: Observable<unknown> = this.currentEvent
      ? this.personService.updateOfficiantCollaboration(this.person.id, this.currentEvent.id, request)
      : this.personService.createOfficiantCollaboration(this.person.id, request);

    operation$.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.notifications.success(this.currentEvent
          ? 'Actividad de oficiante corregida correctamente.'
          : 'Actividad de oficiante registrada correctamente.');
        this.saved.emit();
      },
      error: (err: unknown) => {
        this.isSaving.set(false);
        this.notifications.error(getPersonEventEditorErrorMessage(
          err,
          this.currentEvent
            ? 'No se pudo corregir la actividad de oficiante.'
            : 'No se pudo registrar la actividad de oficiante.'));
      },
    });
  }

  private buildTransitionRequest(): {
    eventType: MinistryEventType;
    effectiveDate: string;
    expectedEndDate?: string;
    actOrganizationUnitId?: string;
    actLocation?: string;
    reason?: string;
    areas: Array<{
      kind: MinisterialActivityKind;
      referenceId: number;
      organizationUnitId?: string | null;
      historicalOrganizationUnitName?: string | null;
    }>;
    reassignments?: ReassignmentPayloadItem[];
  } | null {
    if (!this.selectedAction || this.isNewOfficiantAction || this.hasIncompleteDesignations() || this.hasDuplicateActivityAreas()) {
      return null;
    }

    if (this.form.invalid) {
      return null;
    }

    const raw = this.form.getRawValue();
    let effectiveDate: string;
    let expectedEndDate: string | undefined;
    try {
      effectiveDate = this.toIsoDate(raw.eventDate);
      expectedEndDate = raw.expectedEndDate ? this.toIsoDate(raw.expectedEndDate) : undefined;
    } catch {
      return null;
    }

    if (this.actionDateValidationMessage(this.selectedAction, effectiveDate)) {
      return null;
    }

    const reassignments = this.isReassignment && !this.currentEvent
      ? this.reassignmentPayloadChanges()
      : [];
    if (this.isReassignment && !this.currentEvent && reassignments.length === 0) {
      return null;
    }

    const areas = this.isReassignment && !this.currentEvent
      ? []
      : this.designationPayloadAreas();
    if (this.requiresAreasForPreview() && areas.length === 0) {
      return null;
    }

    return {
      eventType: this.selectedAction,
      effectiveDate,
      expectedEndDate,
      actOrganizationUnitId: this.showEventDetailsFields && raw.actPlaceMode === 'community' ? raw.actOrganizationUnitId ?? undefined : undefined,
      actLocation: this.showEventDetailsFields && raw.actPlaceMode === 'location' ? raw.actLocation?.trim() || undefined : undefined,
      reason: this.showEventDetailsFields ? raw.reason?.trim() || undefined : undefined,
      areas,
      reassignments: this.isReassignment && !this.currentEvent ? reassignments : undefined,
    };
  }

  private requiresAreasForPreview(): boolean {
    return this.selectedActionDefinition?.requiresArea ??
      (this.isReinstatement || this.isReassignment || this.isActivationEvent);
  }

  private mapPreviewDesignations(
    designations: MinisterialActivityTransitionPreview['before']['designations'],
  ): ActivitySummaryItem[] {
    return designations.map(item => ({
      kind: item.kind,
      referenceId: item.referenceId,
      referenceName: item.referenceName,
      organizationUnitId: item.organizationUnitId ?? undefined,
      organizationUnitName: item.organizationUnitName,
      historicalOrganizationUnitName: item.isHistoricalOrganizationUnit ? item.organizationUnitName : undefined,
    }));
  }

  areaKindLabel(kind: MinisterialActivityKind): string {
    return this.kindOptions.find(option => option.value === kind)?.label ?? kind;
  }

  summaryItemState(item: ActivitySummaryItem): 'added' | 'unchanged' {
    const currentKeys = new Set(this.currentSummaryItems.map(current => this.summaryKey(current)));
    return currentKeys.has(this.summaryKey(item)) ? 'unchanged' : 'added';
  }

  private get designationSummaryItems(): ActivitySummaryItem[] {
    const primaryUnitId = this.form.controls.primaryOrganizationUnitId.value;
    const primaryHistoricalName = this.form.controls.primaryHistoricalOrganizationUnitName.value?.trim() || null;
    const result: ActivitySummaryItem[] = [];

    for (let index = 0; index < this.designations.length; index++) {
      const group = this.designations.at(index);
      const kind = group.controls['kind'].value as MinisterialActivityKind | null;
      const referenceId = group.controls['referenceId'].value as number | null;
      if (!kind || !referenceId) {
        continue;
      }

      const referenceName = this.referenceName(kind, referenceId);
      if (this.isReassignment) {
        for (const area of this.specificAreas(index).controls.flatMap(area => this.reassignmentDestinationAreas(area))) {
          const organizationUnitId = area.organizationUnitId;
          const historicalOrganizationUnitName = area.historicalOrganizationUnitName;
          if (organizationUnitId && this.isUnitAllowedForDesignation(organizationUnitId, kind, referenceId)) {
            result.push({ kind, referenceId, referenceName, organizationUnitId, organizationUnitName: this.unitName(organizationUnitId) });
          } else if (historicalOrganizationUnitName) {
            result.push({ kind, referenceId, referenceName, organizationUnitName: historicalOrganizationUnitName, historicalOrganizationUnitName });
          }
        }
        continue;
      }

      if (this.specificAreas(index).length > 0) {
        for (const area of this.specificAreas(index).controls) {
          const organizationUnitId = area.controls['organizationUnitId'].value as string | null;
          const historicalOrganizationUnitName = area.controls['historicalOrganizationUnitName'].value?.trim() || null;
          if (organizationUnitId && this.isUnitAllowedForDesignation(organizationUnitId, kind, referenceId)) {
            result.push({
              kind,
              referenceId,
              referenceName,
              organizationUnitId,
              organizationUnitName: this.unitName(organizationUnitId),
            });
          } else if (historicalOrganizationUnitName) {
            result.push({
              kind,
              referenceId,
              referenceName,
              organizationUnitName: historicalOrganizationUnitName,
              historicalOrganizationUnitName,
            });
          }
        }
      } else if (primaryUnitId && this.isUnitAllowedForDesignation(primaryUnitId, kind, referenceId)) {
        result.push({
          kind,
          referenceId,
          referenceName,
          organizationUnitId: primaryUnitId,
          organizationUnitName: this.unitName(primaryUnitId),
        });
      } else if (primaryHistoricalName) {
        result.push({
          kind,
          referenceId,
          referenceName,
          organizationUnitName: primaryHistoricalName,
          historicalOrganizationUnitName: primaryHistoricalName,
        });
      }
    }

    return this.uniqueSummaryItems(result);
  }

  private get implicitCurrentMinistrySummaryItems(): ActivitySummaryItem[] {
    if (!this.effectiveCurrentMinistry) {
      return [];
    }

    const relatedAreas = this.designationSummaryItems.filter(item => item.kind !== 'Ministry');
    return this.uniqueSummaryItems(relatedAreas.map(item => {
      if (item.organizationUnitId) {
        return {
          kind: 'Ministry',
          referenceId: this.effectiveCurrentMinistry!.ministryId,
          referenceName: this.effectiveCurrentMinistry!.ministryName,
          organizationUnitId: item.organizationUnitId,
          organizationUnitName: item.organizationUnitName,
        };
      }

      return {
        kind: 'Ministry',
        referenceId: this.effectiveCurrentMinistry!.ministryId,
        referenceName: this.effectiveCurrentMinistry!.ministryName,
        organizationUnitName: item.historicalOrganizationUnitName ?? item.organizationUnitName,
        historicalOrganizationUnitName: item.historicalOrganizationUnitName ?? item.organizationUnitName,
      };
    }));
  }

  private switchActionDraft(action: MinistryEventType | null): void {
    if (this.activeDraftAction) {
      this.actionDrafts.set(this.activeDraftAction, {
        values: { ...this.form.getRawValue(), action: this.activeDraftAction },
        designations: this.designations,
        controls: Object.entries(this.form.controls)
          .filter(([name]) => name !== 'action' && name !== 'designations')
          .map(([, control]) => ({
            control, validator: control.validator, asyncValidator: control.asyncValidator,
            errors: control.errors, disabled: control.disabled, dirty: control.dirty, touched: control.touched,
          })),
        appointmentEndSelectionKeys: [...this.appointmentEndSelectionKeys],
        officiantAreaEditing: this.officiantAreaEditing,
      });
    }

    this.form.setControl('designations', this.fb.array<FormGroup>([]), { emitEvent: false });
    this.form.reset({
      action,
      eventDate: this.dateAdapter.today(),
      institutionAreaIds: [],
      primaryAreaMode: 'unit',
      primaryHistoricalOrganizationUnitName: '',
      actPlaceMode: 'community',
      actLocation: '',
      reason: '',
    }, { emitEvent: false });
    this.designationGroupCache.clear();
    this.configureForAction(action);
    const draft = action ? this.actionDrafts.get(action) : undefined;
    if (draft) {
      const { designations, ...values } = draft.values;
      this.form.setControl('designations', draft.designations, { emitEvent: false });
      this.form.patchValue(values, { emitEvent: false });
      for (const state of draft.controls) {
        const control = state.control;
        control.setValidators(state.validator);
        control.setAsyncValidators(state.asyncValidator);
        if (state.disabled) control.disable({ emitEvent: false });
        else control.enable({ emitEvent: false });
        control.setErrors(state.errors, { emitEvent: false });
        if (state.dirty) control.markAsDirty();
        if (state.touched) control.markAsTouched();
      }
      if (draft.designations.dirty) this.form.markAsDirty();
      if (draft.designations.touched) this.form.markAsTouched();
      this.appointmentEndSelectionKeys.clear();
      for (const key of draft.appointmentEndSelectionKeys) this.appointmentEndSelectionKeys.add(key);
      this.officiantAreaEditing = draft.officiantAreaEditing;
      this.form.updateValueAndValidity({ emitEvent: false });
    }
    this.activeDraftAction = action;
    this.preserveDraftOnNextState = !!draft;
    this.cdr.markForCheck();
  }

  private configureForAction(action: MinistryEventType | null): void {
    this.designations.clear();
    this.appointmentEndSelectionKeys.clear();
    this.officiantAreaEditing = false;
    const primaryControl = this.form.controls.primaryOrganizationUnitId;
    const expectedEndDateControl = this.form.controls.expectedEndDate;
    const reasonControl = this.form.controls.reason;
    const institutionAreaIdsControl = this.form.controls.institutionAreaIds;
    institutionAreaIdsControl.clearValidators();
    institutionAreaIdsControl.enable({ emitEvent: false });
    institutionAreaIdsControl.setValue([], { emitEvent: false });
    this.form.controls.membershipCommunityId.setValue(null, { emitEvent: false });
    this.form.controls.membershipCommunityId.clearValidators();
    this.form.controls.membershipCommunityId.updateValueAndValidity({ emitEvent: false });
    primaryControl.clearValidators();
    expectedEndDateControl.clearValidators();
    reasonControl.setValidators([Validators.maxLength(500)]);

    const definition = action ? this.selectedActionDefinition : undefined;
    if (definition?.requiresExpectedEndDate) {
      expectedEndDateControl.setValidators([Validators.required]);
    }
    if (definition?.requiresComment) {
      reasonControl.setValidators([Validators.required, Validators.maxLength(500)]);
    }

    if (action === 'NewOfficiant') {
      this.form.controls.actPlaceMode.setValue('community', { emitEvent: false });
      this.form.controls.actOrganizationUnitId.setValue(this.person.communityId ?? null, { emitEvent: false });
      this.form.controls.actLocation.setValue('', { emitEvent: false });
    }

    if (this.isSingleMinistryAssignmentAction) {
      this.form.controls.primaryAreaMode.setValue('unit');
      primaryControl.setValue(null);
      this.addDesignation({ kind: 'Ministry', referenceId: null });
    } else if (definition?.requiresAppointmentOrAssignment) {
      this.form.controls.primaryAreaMode.setValue('unit');
      primaryControl.setValue(this.suggestedPrimaryUnitId());
      if (definition.eventType !== 'AppointmentEnd') {
        this.addDesignation();
      }
    } else if (definition?.requiresMinistry) {
      this.form.controls.primaryAreaMode.setValue('unit');
      primaryControl.setValue(this.suggestedPrimaryUnitId());
      this.addDesignation({ kind: 'Ministry', referenceId: null });
    } else if (action === 'Reinstatement') {
      this.form.controls.primaryAreaMode.setValue('unit');
      this.form.controls.primaryHistoricalOrganizationUnitName.setValue('');
      primaryControl.setValue(null);
      this.seedDesignations(this.reinstatementQuickAreas, null, false);
    } else if (action === 'Ordination') {
      const primaryUnitId = this.suggestedPrimaryUnitId();
      this.form.controls.primaryAreaMode.setValue('unit');
      this.form.controls.primaryHistoricalOrganizationUnitName.setValue('');
      primaryControl.setValue(primaryUnitId);
      this.addDesignation();
    } else if (action === 'Reassignment') {
      this.form.controls.primaryAreaMode.setValue('unit');
      this.form.controls.primaryHistoricalOrganizationUnitName.setValue('');
      primaryControl.setValue(null);
      this.seedReassignmentDesignation();
    } else {
      primaryControl.setValue(null);
      this.form.controls.primaryHistoricalOrganizationUnitName.setValue('');
    }

    primaryControl.updateValueAndValidity();
    if (this.isInstitutionAction) {
      institutionAreaIdsControl.setValidators([Validators.required, Validators.minLength(1)]);
      institutionAreaIdsControl.disable({ emitEvent: false });
    }
    institutionAreaIdsControl.updateValueAndValidity({ emitEvent: false });
    expectedEndDateControl.updateValueAndValidity();
    reasonControl.updateValueAndValidity();
    this.syncActPlaceValidation();
    this.syncEventActPlaceControl();
    this.presetLicenseEndDate();
  }

  private presetLicenseEndDate(): void {
    const control = this.form.controls.expectedEndDate;
    const startDate = this.form.controls.eventDate.value;
    if (this.isLicenseEndDateAction && !this.currentEvent && control.pristine &&
        startDate && this.dateAdapter.isValid(startDate)) {
      control.setValue(this.dateAdapter.addCalendarMonths(startDate, 6), { emitEvent: false });
    }
  }

  private updateMinisterialMobileLayout(): void {
    this.isMinisterialMobileLayout = typeof window !== 'undefined' && window.innerWidth <= 850;
  }

  private normalizeActionFilterText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('es')
      .trim();
  }

  private seedReassignmentDesignation(): void {
    const ministry = this.effectiveCurrentMinistry;
    if (!ministry) {
      return;
    }

    const ministryAreas = this.effectiveCurrentAreas
      .filter(area => area.kind === 'Ministry' && area.referenceId === ministry.ministryId)
      .map(area => ({
        organizationUnitId: area.organizationUnitId ?? null,
        historicalOrganizationUnitName: area.isHistoricalOrganizationUnit ? area.organizationUnitName : null,
      }));

    this.designations.push(this.createDesignationGroup(
      'Ministry',
      ministry.ministryId,
      true,
      ministryAreas.length > 0
        ? ministryAreas
        : [{ organizationUnitId: null, historicalOrganizationUnitName: null }],
    ));
  }

  private reseedReinstatementDesignation(): void {
    if (!this.isReinstatement) {
      return;
    }

    if (this.designations.dirty) {
      return;
    }

    this.designations.clear();
    if (!this.isReinstatementDateValid) {
      return;
    }

    const licenseAreas = this.reinstatementQuickAreas;
    if (licenseAreas.length > 0) {
      this.seedDesignations(licenseAreas, null, false);
      return;
    }

    const ministry = this.effectiveCurrentMinistry;
    if (!ministry) {
      return;
    }

    this.designations.push(this.createDesignationGroup(
      'Ministry',
      ministry.ministryId,
      true,
      [{ organizationUnitId: null, historicalOrganizationUnitName: null }],
    ));
  }

  private reseedReassignmentDesignation(): void {
    if (this.designations.dirty || !this.effectiveCurrentMinistry) {
      return;
    }

    this.designations.clear();
    const hasMinistryAreas = this.effectiveCurrentAreas.some(area =>
      area.kind === 'Ministry' && area.referenceId === this.effectiveCurrentMinistry!.ministryId);
    if (!hasMinistryAreas) {
      this.designations.push(this.createDesignationGroup(
        'Ministry',
        this.effectiveCurrentMinistry.ministryId,
        true,
        [{ organizationUnitId: null, historicalOrganizationUnitName: null }],
        true,
      ));
      return;
    }

    this.seedReassignmentDesignation();
  }

  private initializeCorrection(): void {
    if (!this.currentEvent) {
      return;
    }

    const action = this.currentEvent.eventTypeKey as MinistryEventType;
    const persistedEventAreas = this.currentEvent.ministerialAreas
      .filter(area => area.changeType === 'Added');
    const eventAreas = (action === 'Reassignment' && persistedEventAreas.length === 0
      ? this.currentAreas
      : persistedEventAreas).filter(area => action !== 'AppointmentAssignment' || area.kind !== 'Ministry');
    const primaryUnitId = eventAreas[0]?.organizationUnitId ?? null;
    const primaryHistoricalName = eventAreas[0]?.isHistoricalOrganizationUnit
      ? eventAreas[0].organizationUnitName
      : '';

    this.form.patchValue({
      action,
      eventDate: this.dateAdapter.deserialize(this.currentEvent.eventDate),
      expectedEndDate: this.currentEvent.endDate ? this.dateAdapter.deserialize(this.currentEvent.endDate) : null,
      primaryOrganizationUnitId: primaryUnitId,
      primaryAreaMode: primaryHistoricalName ? 'historical' : 'unit',
      primaryHistoricalOrganizationUnitName: primaryHistoricalName,
      actPlaceMode: this.currentEvent.actOrganizationUnitId ? 'community' : 'location',
      actOrganizationUnitId: this.currentEvent.actOrganizationUnitId ?? null,
      actLocation: this.currentEvent.location ?? '',
      reason: this.currentEvent.notes ?? '',
    });

    this.configureForAction(action);
    if (action === 'NewOfficiant') {
      this.form.controls.actPlaceMode.setValue('community', { emitEvent: false });
      this.form.controls.actOrganizationUnitId.setValue(this.currentEvent.organizationUnitId ?? null, { emitEvent: false });
      this.form.controls.actLocation.setValue('', { emitEvent: false });
    }

    if (this.requiresDesignations) {
      this.form.controls.primaryOrganizationUnitId.setValue(primaryUnitId);
      if (primaryHistoricalName) {
        this.form.controls.primaryAreaMode.setValue('historical');
        this.form.controls.primaryHistoricalOrganizationUnitName.setValue(primaryHistoricalName);
        this.onPrimaryAreaModeChanged();
      }
      this.designations.clear();
      this.seedDesignations(eventAreas, primaryUnitId);
      if (this.isInstitutionAction) {
        this.form.controls.institutionAreaIds.enable({ emitEvent: false });
        const areaIds = eventAreas
          .filter(area => area.kind === 'Ministry')
          .map(area => area.organizationUnitId ?? area.organizationUnitName);
        this.specificAreas(0).clear();
        this.form.controls.institutionAreaIds.setValue(areaIds, { emitEvent: false });
        this.onInstitutionAreasChanged(areaIds);
      }
    }

    this.form.controls.primaryOrganizationUnitId.updateValueAndValidity();
    this.syncActPlaceValidation();
    this.syncEventActPlaceControl();
  }

  private seedDesignations(areasToSeed: MinisterialActivityArea[], primaryUnitId: string | null, editAreas = true): void {
    const grouped = new Map<string, MinisterialActivityArea[]>();
    for (const area of areasToSeed) {
      const key = this.referenceKey(area.kind, area.referenceId);
      grouped.set(key, [...(grouped.get(key) ?? []), area]);
    }

    for (const areas of grouped.values()) {
      const first = areas[0];
      const usesDifferentArea = areas.length !== 1
        || areas[0].organizationUnitId !== primaryUnitId
        || (!!areas[0].isHistoricalOrganizationUnit && !this.isHistoricalPrimaryArea);
      const group = this.createDesignationGroup(
        first.kind,
        first.referenceId,
        usesDifferentArea,
        usesDifferentArea || this.isAppointmentAssignmentAction || this.isReinstatement
          ? areas.map(area => ({
            organizationUnitId: area.organizationUnitId ?? null,
            historicalOrganizationUnitName: area.isHistoricalOrganizationUnit ? area.organizationUnitName : null,
          }))
          : [],
        editAreas);
      group.controls['designationEndDate'].setValue(first.validTo ? this.dateAdapter.deserialize(first.validTo) : null);
      this.designations.push(group);
    }
  }

  private createDesignationGroup(
    kind: MinisterialActivityKind | null,
    referenceId: number | null,
    usesDifferentArea: boolean,
    areas: Array<{ organizationUnitId: string | null; historicalOrganizationUnitName: string | null }>,
    editAreas = true,
  ): FormGroup {
    if (this.isAppointmentAssignmentAction && !referenceId) {
      areas = [];
    }
    const usesMultipleUnits = this.isReinstatement || (this.isAppointmentAssignmentAction && !areas.some(area => area.historicalOrganizationUnitName));
    return this.fb.group({
      kind: [kind, Validators.required],
      referenceId: [referenceId, Validators.required],
      activitySearch: [this.activitySelectionValue(kind, referenceId)],
      designationEditing: [false],
      designationEndDate: new FormControl<any>(null, control => {
        if (!this.isAppointmentAssignmentAction || !control.value || !this.form.controls.eventDate.value) return null;
        try {
          return this.toIsoDate(control.value) > this.toIsoDate(this.form.controls.eventDate.value) ? null : { endBeforeStart: true };
        } catch {
          return { invalidDate: true };
        }
      }),
      usesDifferentArea: [usesDifferentArea],
      selectedAreaIds: new FormControl<string[]>({
        value: areas.flatMap(area => area.organizationUnitId ? [area.organizationUnitId]
          : this.isReinstatement && area.historicalOrganizationUnitName ? [area.historicalOrganizationUnitName] : []),
        disabled: (this.isAppointmentAssignmentAction || this.isReinstatement) && !referenceId,
      }, { nonNullable: true, validators: usesMultipleUnits ? [Validators.required, Validators.minLength(1)] : [] }),
      specificAreas: this.fb.array<FormGroup>(
        areas.map(area => this.createSpecificAreaGroup(
          area.organizationUnitId,
          area.historicalOrganizationUnitName,
          editAreas))),
    });
  }

  private appointmentEndSelectionKey(designation: MinisterialActivityEffectiveDesignation): string {
    return [
      designation.kind,
      designation.referenceId,
      designation.organizationUnitId ?? '',
      designation.isHistoricalOrganizationUnit ? designation.organizationUnitName.trim() : '',
    ].join('|');
  }

  private createSpecificAreaGroup(
    organizationUnitId: string | null,
    historicalOrganizationUnitName: string | null = null,
    isEditing = true,
  ): FormGroup {
    const isHistorical = !!historicalOrganizationUnitName;
    return this.fb.group({
      areaMode: [isHistorical ? 'historical' : 'unit' as 'unit' | 'historical'],
      isEditing: [isEditing],
      sourceOrganizationUnitId: [organizationUnitId],
      sourceHistoricalOrganizationUnitName: [historicalOrganizationUnitName?.trim() || null],
      destinations: new FormControl<string[]>(
        this.isReassignment && !this.currentEvent ? []
          : organizationUnitId ? [organizationUnitId] : historicalOrganizationUnitName ? [historicalOrganizationUnitName.trim()] : [],
        { nonNullable: true, validators: this.isReassignment ? [
          Validators.required,
          control => (control.value as string[]).some(value => value.length > 200) ? { historicalNameTooLong: true } : null,
        ] : [] }),
      organizationUnitId: [organizationUnitId, isHistorical ? [] : Validators.required],
      historicalOrganizationUnitName: [
        historicalOrganizationUnitName ?? '',
        isHistorical ? [Validators.required, Validators.maxLength(200)] : [Validators.maxLength(200)],
      ],
    });
  }

  private activityOptionsForKind(kind: MinisterialActivityKind): ActivitySelectionOption[] {
    const source = this.isReassignment
      ? this.currentReferences.filter(reference => reference.kind === kind)
      : kind === 'Ministry'
        ? this.ministries
        : kind === 'Appointment'
          ? this.appointments
          : this.assignments;

    return source
      .map(option => ({ kind, id: option.id, name: option.name, isActive: option.isActive !== false }));
  }

  activitySelectionValue(kind: MinisterialActivityKind | null, referenceId: number | null): string | null {
    return kind && referenceId ? `${kind}:${referenceId}` : null;
  }

  private isActivitySelectionValue(value: string): boolean {
    return this.parseActivitySelectionValue(value) !== null;
  }

  private parseActivitySelectionValue(
    value: string | null,
  ): { kind: MinisterialActivityKind; referenceId: number } | null {
    if (!value) {
      return null;
    }

    const [kindValue, referenceIdValue] = value.split(':');
    const kind = this.kindOptions.some(option => option.value === kindValue)
      ? kindValue as MinisterialActivityKind
      : null;
    const referenceId = Number(referenceIdValue);
    return kind && Number.isInteger(referenceId)
      ? { kind, referenceId }
      : null;
  }

  private normalizeActivitySearch(value: string): string {
    return value.trim().toLocaleLowerCase('es').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  private designationPayloadAreas(): Array<{
    kind: MinisterialActivityKind;
    referenceId: number;
    organizationUnitId: string | null;
    historicalOrganizationUnitName: string | null;
    expectedEndDate?: string;
  }> {
    const primaryOrganizationUnitId = this.form.controls.primaryOrganizationUnitId.value;
    const primaryHistoricalOrganizationUnitName = this.form.controls.primaryHistoricalOrganizationUnitName.value?.trim() || null;
    if (this.isInstitutionAction) {
      const ministryId = this.institutionMinistryId;
      return ministryId
        ? this.selectedUnitAreas(this.form.controls.institutionAreaIds.value ?? []).map(area => ({
          kind: 'Ministry' as MinisterialActivityKind,
          referenceId: ministryId,
          ...area,
        }))
        : [];
    }

    const payloadAreas = this.designations.controls.flatMap((group, index) => {
      const kind = group.controls['kind'].value as MinisterialActivityKind | null;
      const referenceId = group.controls['referenceId'].value as number | null;
      if (!kind || !referenceId) {
        return [];
      }

      let expectedEndDate: string | undefined;
      const endDateControl = group.controls['designationEndDate'];
      if (this.isAppointmentAssignmentAction && endDateControl.value && endDateControl.valid) {
        expectedEndDate = this.toIsoDate(endDateControl.value);
      }

      if (this.isReassignment) {
        return this.specificAreas(index).controls.flatMap(area => this.reassignmentDestinationAreas(area).map(destination => ({
          kind,
          referenceId,
          ...destination,
        })));
      }

      if (this.specificAreas(index).length === 0) {
        return [{
          kind,
          referenceId,
          expectedEndDate,
          organizationUnitId: this.isHistoricalPrimaryArea ? null : primaryOrganizationUnitId,
          historicalOrganizationUnitName: this.isHistoricalPrimaryArea ? primaryHistoricalOrganizationUnitName : null,
        }];
      }

      return this.specificAreas(index).controls.map(area => ({
        kind,
        referenceId,
        expectedEndDate,
        organizationUnitId: area.controls['areaMode'].value === 'historical'
          ? null
          : area.controls['organizationUnitId'].value as string | null,
        historicalOrganizationUnitName: area.controls['areaMode'].value === 'historical'
          ? area.controls['historicalOrganizationUnitName'].value?.trim() || null
          : null,
      }));
    });

    if (this.ordinationMovesCurrentMinistry && this.effectiveCurrentMinistry) {
      const implicitAreas = payloadAreas
        .filter(area => area.kind !== 'Ministry')
        .map(area => ({
          kind: 'Ministry' as MinisterialActivityKind,
          referenceId: this.effectiveCurrentMinistry!.ministryId,
          organizationUnitId: area.organizationUnitId,
          historicalOrganizationUnitName: area.historicalOrganizationUnitName,
        }));
      payloadAreas.unshift(...implicitAreas);
    }

    return payloadAreas;
  }

  private reassignmentPayloadChanges(): ReassignmentPayloadItem[] {
    if (!this.isReassignment) {
      return [];
    }

    return this.designations.controls.flatMap((group, index) => {
      const kind = group.controls['kind'].value as MinisterialActivityKind | null;
      const referenceId = group.controls['referenceId'].value as number | null;
      if (!kind || !referenceId) {
        return [];
      }

      return this.specificAreas(index).controls.flatMap(area => {
        if (!area.controls['isEditing']?.value) {
          return [];
        }

        const source = {
          kind,
          referenceId,
          organizationUnitId: area.controls['sourceOrganizationUnitId']?.value as string | null,
          historicalOrganizationUnitName: area.controls['sourceHistoricalOrganizationUnitName']?.value as string | null,
        };
        const destinations = this.reassignmentDestinationAreas(area);
        const retainsSource = destinations.some(destination => this.samePayloadArea(source, { kind, referenceId, ...destination }));
        return destinations.filter(destination => !this.samePayloadArea(source, { kind, referenceId, ...destination }))
          .map((destination, destinationIndex) => ({
          kind,
          referenceId,
          fromOrganizationUnitId: !retainsSource && destinationIndex === 0 ? source.organizationUnitId : null,
          fromHistoricalOrganizationUnitName: !retainsSource && destinationIndex === 0 ? source.historicalOrganizationUnitName : null,
          toOrganizationUnitId: destination.organizationUnitId,
          toHistoricalOrganizationUnitName: destination.historicalOrganizationUnitName,
        }));
      });
    });
  }

  private samePayloadArea(
    left: {
      kind: MinisterialActivityKind;
      referenceId: number;
      organizationUnitId?: string | null;
      historicalOrganizationUnitName?: string | null;
    },
    right: {
      kind: MinisterialActivityKind;
      referenceId: number;
      organizationUnitId?: string | null;
      historicalOrganizationUnitName?: string | null;
    },
  ): boolean {
    return left.kind === right.kind &&
      left.referenceId === right.referenceId &&
      (left.organizationUnitId ?? null) === (right.organizationUnitId ?? null) &&
      (left.historicalOrganizationUnitName?.trim() ?? '') === (right.historicalOrganizationUnitName?.trim() ?? '');
  }

  private hasDuplicateActivityAreas(): boolean {
    const keys = this.designationPayloadAreas()
      .filter(area => !!area.kind && !!area.referenceId)
      .map(area => [
        area.kind,
        area.referenceId,
        area.organizationUnitId ?? '',
        area.historicalOrganizationUnitName ?? '',
      ].join(':'));
    return new Set(keys).size !== keys.length;
  }

  private hasInvalidDesignationAreas(): boolean {
    return this.designations.controls.some((group, index) => {
      const kind = group.controls['kind'].value as MinisterialActivityKind | null;
      const referenceId = group.controls['referenceId'].value as number | null;
      if (this.isReassignment) {
        return this.specificAreas(index).controls.flatMap(area => this.reassignmentDestinationAreas(area))
          .some(area => !!area.organizationUnitId && !this.isUnitAllowedForDesignation(area.organizationUnitId, kind, referenceId));
      }
      return this.specificAreas(index).controls.some(area => {
        if (area.controls['areaMode'].value === 'historical') {
          return false;
        }

        const unitId = area.controls['organizationUnitId'].value as string | null;
        return !!unitId && !this.isUnitAllowedForDesignation(unitId, kind, referenceId);
      });
    });
  }

  private hasIncompleteDesignations(): boolean {
    if (this.isInstitutionAction) {
      return this.designations.length === 0
        || this.designations.controls.some(group => group.invalid)
        || (this.form.controls.institutionAreaIds.value ?? []).length === 0;
    }

    if (this.isReassignment) {
      return this.designations.length === 0
        || this.specificAreas(0).length === 0
        || this.designations.controls.some(group => group.invalid);
    }

    return this.designationsRequired
      && (this.designations.length === 0
        || this.designations.controls.some(group => group.invalid));
  }

  private defaultAreaForNewDesignation(): { organizationUnitId: string | null; historicalOrganizationUnitName: string | null } | null {
    if (this.isReassignment || this.designations.length > 0) {
      return null;
    }

    const primaryUnitId = this.form.controls.primaryOrganizationUnitId.value ?? this.suggestedPrimaryUnitId();
    if (primaryUnitId) {
      return { organizationUnitId: primaryUnitId, historicalOrganizationUnitName: null };
    }

    return null;
  }

  private defaultAreaForDesignation(
    kind: MinisterialActivityKind | null,
    referenceId: number | null,
  ): { organizationUnitId: string | null; historicalOrganizationUnitName: string | null } | null {
    const defaultArea = this.defaultAreaForNewDesignation();
    if (!defaultArea?.organizationUnitId) {
      return defaultArea;
    }

    return this.isUnitAllowedForDesignation(defaultArea.organizationUnitId, kind, referenceId)
      ? defaultArea
      : null;
  }

  private clearAreasNotAllowedForDesignation(index: number): void {
    const group = this.designations.at(index);
    const kind = group.controls['kind'].value as MinisterialActivityKind | null;
    const referenceId = group.controls['referenceId'].value as number | null;
    const areas = this.specificAreas(index);
    let clearedAnyArea = false;

    for (const area of areas.controls) {
      const unitId = area.controls['organizationUnitId'].value as string | null;
      if (!unitId || this.isUnitAllowedForDesignation(unitId, kind, referenceId)) {
        if (unitId) {
          this.ensureKnownUnitLevel(unitId, index);
        }
        continue;
      }

      area.controls['organizationUnitId'].setValue(null);
      area.controls['organizationUnitId'].markAsTouched();
      clearedAnyArea = true;
    }

    if (clearedAnyArea) {
      group.controls['usesDifferentArea'].setValue(true);
    }
  }

  private syncAreasForSelectedDesignation(index: number): void {
    const group = this.designations.at(index);
    const kind = group.controls['kind'].value as MinisterialActivityKind | null;
    const referenceId = group.controls['referenceId'].value as number | null;
    const areas = this.specificAreas(index);

    if (areas.length === 0) {
      const defaultArea = this.defaultAreaForDesignation(kind, referenceId);
      this.addSpecificArea(index, defaultArea?.organizationUnitId ?? null, defaultArea?.historicalOrganizationUnitName ?? null);
    }

    this.clearAreasNotAllowedForDesignation(index);
    const hasEmptyCurrentArea = areas.controls.some(area =>
      area.controls['areaMode'].value !== 'historical' && !area.controls['organizationUnitId'].value);
    group.controls['usesDifferentArea'].setValue(hasEmptyCurrentArea);
  }

  private isUnitAllowedForDesignation(
    unitId: string,
    kind: MinisterialActivityKind | null,
    referenceId: number | null,
  ): boolean {
    const allowedLevelIds = this.allowedLevelIdsForDesignation(kind, referenceId);
    if (allowedLevelIds.length === 0) {
      return true;
    }

    const levelId = this.unitLevelIds.get(unitId)
      ?? this.units.find(candidate => candidate.id === unitId)?.levelId;
    return !!levelId && allowedLevelIds.includes(levelId);
  }

  private allowedLevelIdsForDesignation(
    kind: MinisterialActivityKind | null,
    referenceId: number | null,
  ): number[] {
    const option = this.getCatalogOption(kind, referenceId);
    const levelIds = this.readAllowedLevelIds(option);
    if (levelIds.length > 0) {
      return levelIds;
    }

    return kind === 'Appointment' && this.isDistrictApostleAppointment(option?.name)
      ? [REGIONAL_CHURCH_LEVEL_ID]
      : this.noAllowedLevelIds;
  }

  private readAllowedLevelIds(option: ReferenceItem | undefined): number[] {
    const camelCaseLevelIds = option?.allowedOrganizationLevelIds ?? [];
    if (camelCaseLevelIds.length > 0) {
      return camelCaseLevelIds;
    }

    return ((option as ReferenceItem & { AllowedOrganizationLevelIds?: number[] } | undefined)
      ?.AllowedOrganizationLevelIds) ?? [];
  }

  private isDistrictApostleAppointment(name: string | undefined): boolean {
    return this.normalizeActivitySearch(name ?? '') === DISTRICT_APOSTLE_APPOINTMENT_NAME;
  }

  private ensureKnownUnitLevel(unitId: string, designationIndex: number): void {
    if (this.unitLevelIds.has(unitId)) {
      return;
    }

    this.organizationUnitService.getById(unitId).subscribe({
      next: unit => {
        this.registerKnownUnit({
          id: unit.id,
          name: unit.name,
          levelId: unit.levelId ?? unit.organizationLevelId ?? 0,
          levelName: unit.levelName ?? '',
          parentId: unit.parentUnitId,
          ancestorNames: unit.ancestorNames,
        });
        this.clearAreasNotAllowedForDesignation(designationIndex);
        this.cdr.markForCheck();
      },
      error: () => {
        this.clearAreasNotAllowedForDesignation(designationIndex);
        this.cdr.markForCheck();
      },
    });
  }

  private getCatalogOption(kind: MinisterialActivityKind | null, referenceId: number | null): ReferenceItem | undefined {
    if (!kind || !referenceId) {
      return undefined;
    }

    const source = kind === 'Ministry'
      ? this.ministries
      : kind === 'Appointment'
        ? this.appointments
        : this.assignments;
    return source.find(option => option.id === referenceId);
  }

  private referenceName(kind: MinisterialActivityKind, referenceId: number): string {
    return this.getCatalogOption(kind, referenceId)?.name
      ?? this.currentReferences.find(reference => reference.kind === kind && reference.id === referenceId)?.name
      ?? 'Actividad sin identificar';
  }

  private ministryName(ministryId: number): string {
    const eventArea = this.ministryEvents
      .flatMap(event => event.ministerialAreas ?? [])
      .find(area => area.kind === 'Ministry' && area.referenceId === ministryId);

    return this.getCatalogOption('Ministry', ministryId)?.name
      ?? eventArea?.referenceName
      ?? (this.person.ministry?.ministryId === ministryId ? this.person.ministry.ministryName : 'Ministerio sin identificar');
  }

  designationName(index: number): string {
    const designation = this.designations.at(index);
    return this.referenceName(
      designation.controls['kind'].value as MinisterialActivityKind,
      designation.controls['referenceId'].value as number,
    );
  }

  private suggestedPrimaryUnitId(): string | null {
    const ministryArea = this.effectiveCurrentAreas.find(area => area.kind === 'Ministry');
    return ministryArea?.organizationUnitId
      ?? this.effectiveCurrentAreas[0]?.organizationUnitId
      ?? this.person.communityId
      ?? null;
  }

  private registerKnownUnits(): void {
    if (this.person.communityId && this.person.communityName) {
      this.unitNames.set(this.person.communityId, this.person.communityName);
    }
    for (const unit of this.units) {
      this.registerKnownUnit(unit);
    }
    for (const event of this.ministryEvents) {
      for (const area of event.ministerialAreas ?? []) {
        if (area.organizationUnitId) {
          this.unitNames.set(area.organizationUnitId, area.organizationUnitName);
        }
      }
    }
    for (const area of this.effectiveCurrentAreas) {
      if (area.organizationUnitId) {
        this.unitNames.set(area.organizationUnitId, area.organizationUnitName);
      }
    }
  }

  private registerKnownUnit(unit: OrganizationUnitItem): void {
    this.knownUnits.set(unit.id, unit);
    this.unitNames.set(unit.id, unit.name);
    this.unitLevelIds.set(unit.id, unit.levelId);
  }

  private unitName(unitId: string): string {
    return this.unitNames.get(unitId) ?? 'Unidad seleccionada';
  }

  private referenceKey(kind: MinisterialActivityKind, referenceId: number): string {
    return `${kind}:${referenceId}`;
  }

  private summaryKey(item: ActivitySummaryItem): string {
    return `${item.kind}:${item.referenceId}:${item.organizationUnitId ?? `historical:${item.historicalOrganizationUnitName ?? ''}`}`;
  }

  private get selectedReferenceDate(): string | null {
    const value = this.form.controls.eventDate.value;
    if (!value) {
      return null;
    }

    try {
      return this.toIsoDate(value);
    } catch {
      return null;
    }
  }

  private get sortedMinistryEvents(): PersonEvent[] {
    return [...this.ministryEvents].sort((left, right) =>
      (left.eventDate ?? '').localeCompare(right.eventDate ?? '') ||
      left.id - right.id);
  }

  private ministryStatusAtDate(referenceDate: string): string | undefined {
    const serverState = this.serverEffectiveStateForDate(referenceDate);
    return serverState?.status;
  }

  private actionDateValidationMessage(action: MinistryEventType, referenceDate: string): string {
    const status = this.ministryStatusAtDate(referenceDate);

    switch (action) {
      case 'Reassignment':
        return '';
      case 'Reinstatement':
        return status === 'Licensed'
          ? ''
          : 'Elegí una fecha en la que el ministerio esté de licencia.';
      case 'MinisterialLicenseRenewal':
        return status === 'Licensed'
          ? ''
          : 'Elegí una fecha en la que el ministerio esté de licencia.';
      case 'LeaveOfAbsence':
        return status === 'Active'
          ? ''
          : 'Elegí una fecha en la que el ministerio esté en actividad.';
      case 'Retirement':
        return status === 'Active' || status === 'Licensed'
          ? ''
          : 'Elegí una fecha en la que el ministerio esté en actividad o de licencia.';
      case 'Dismissal':
      case 'Resignation':
        return status === 'Active' || status === 'Licensed' || status === 'Retired'
          ? ''
          : 'Elegí una fecha en la que el ministerio esté vigente.';
      default:
        return '';
    }
  }

  private get serverEffectiveStateForSelectedDate(): MinisterialActivityEffectiveState | null {
    const referenceDate = this.selectedReferenceDate;
    return referenceDate ? this.serverEffectiveStateForDate(referenceDate) : null;
  }

  private serverEffectiveStateForDate(referenceDate: string): MinisterialActivityEffectiveState | null {
    return this.effectiveStateDate === referenceDate && this.effectiveStateFromApi
      ? {
          ...this.effectiveStateFromApi,
          effectiveDate: this.effectiveStateFromApi.effectiveDate || referenceDate,
          designations: this.safeEffectiveDesignations(this.effectiveStateFromApi),
        }
      : null;
  }

  private safeEffectiveDesignations(
    state: MinisterialActivityEffectiveState | null,
  ): MinisterialActivityEffectiveState['designations'] {
    return Array.isArray(state?.designations) ? state.designations : [];
  }

  private isSimpleMinistrySummary(items: ActivitySummaryItem[], ministryName: string): boolean {
    return items.length > 0 &&
      items.every(item => item.kind === 'Ministry' && item.referenceName === ministryName);
  }

  private summaryAreaNames(items: ActivitySummaryItem[]): string {
    return [...new Set(items.map(item => item.organizationUnitName))].join(', ');
  }

  private uniqueSummaryItems(items: ActivitySummaryItem[]): ActivitySummaryItem[] {
    return [...new Map(items.map(item => [this.summaryKey(item), item])).values()];
  }

  private statusLabel(status?: string): string {
    switch (status) {
      case 'Active': return 'En actividad';
      case 'Licensed': return 'De licencia';
      case 'Retired': return 'En descanso';
      case 'Terminated': return 'Finalizado';
      default: return 'Sin ministerio';
    }
  }

  private syncEventActPlaceControl(): void {
    const control = this.form.controls.eventActPlace;
    control.setValidators([
      ...(this.showEventDetailsFields && !this.requiresOfficiantArea && this.requiresActPlace ? [Validators.required] : []),
      control => control.value && this.actPlaceMode === 'none' ? { unconfirmedPlace: true } : null,
    ]);
    control.setValue(this.showEventDetailsFields && !this.requiresOfficiantArea
      ? this.actPlaceMode === 'location'
        ? this.form.controls.actLocation.value || null
        : this.form.controls.actOrganizationUnitId.value
      : null, { emitEvent: false });
  }

  private syncActPlaceValidation(): void {
    const communityControl = this.form.controls.actOrganizationUnitId;
    const locationControl = this.form.controls.actLocation;
    communityControl.clearValidators();
    locationControl.setValidators([Validators.maxLength(200)]);

    if ((this.requiresActPlace || this.requiresOfficiantArea) && this.actPlaceMode === 'community') {
      communityControl.setValidators([Validators.required]);
    } else if (this.requiresActPlace && this.actPlaceMode === 'location') {
      locationControl.setValidators([Validators.required, Validators.maxLength(200)]);
    }

    if (this.actPlaceMode === 'community') {
      locationControl.setValue('');
    } else if (this.actPlaceMode === 'location') {
      communityControl.setValue(null);
    } else {
      communityControl.setValue(null);
      locationControl.setValue('');
    }

    communityControl.updateValueAndValidity();
    locationControl.updateValueAndValidity();
  }

  private toIsoDate(value: any): string {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
      if (isoMatch) {
        this.ensureValidDateParts(Number(isoMatch[1]), Number(isoMatch[2]), Number(isoMatch[3]));
        return trimmed;
      }

      const localMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
      if (localMatch) {
        const day = Number(localMatch[1]);
        const month = Number(localMatch[2]);
        const year = Number(localMatch[3]);
        this.ensureValidDateParts(year, month, day);
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }

      throw new Error('Invalid date');
    }

    if (!this.dateAdapter.isDateInstance(value) || !this.dateAdapter.isValid(value)) {
      throw new Error('Invalid date');
    }

    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    this.ensureValidDateParts(year, month, day);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private ensureValidDateParts(year: number, month: number, day: number): void {
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day) ||
        year < 1000 || month < 1 || month > 12 || day < 1 || day > 31) {
      throw new Error('Invalid date');
    }

    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      throw new Error('Invalid date');
    }
  }
}
